import { z } from "zod";

export const roleSchema = z.enum(["parent", "student"]);
export type Role = z.infer<typeof roleSchema>;

export const accountabilityModeSchema = z.enum([
  "coach",
  "escalate",
  "enforce",
]);
export const supportLevelSchema = z.enum([
  "high",
  "standard",
  "light",
  "independent",
]);

export const parentLoginSchema = z.object({
  email: z.email().transform((value) => value.trim().toLowerCase()),
  password: z.string().min(12).max(256),
});

export const studentPairingSchema = z.object({
  code: z.union([z.string().regex(/^\d{6}$/), z.string().min(8).max(64)]),
});

export const studentPairingCodeIssuedSchema = z
  .object({
    code: z.string().regex(/^\d{6}$/),
    expiresAt: z.iso.datetime({ offset: true }),
    expiresInSeconds: z.literal(60),
    acceptedRevision: z.number().int().positive(),
    sequence: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
  })
  .strict();
export type StudentPairingCodeIssued = z.infer<
  typeof studentPairingCodeIssuedSchema
>;

export const mutationEnvelopeSchema = z.object({
  requestId: z.uuid(),
  expectedRevision: z.number().int().nonnegative(),
});
export type MutationEnvelope = z.infer<typeof mutationEnvelopeSchema>;

const nonblankText = (maximum: number) => z.string().trim().min(1).max(maximum);

const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

const httpUrlSchema = z.url().refine((value) => {
  const parsed = new URL(value);
  return (
    (parsed.protocol === "http:" || parsed.protocol === "https:") &&
    parsed.username === "" &&
    parsed.password === ""
  );
}, "Resource URL must use HTTP or HTTPS without embedded credentials");

const timezoneSchema = nonblankText(100).refine((value) => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}, "Timezone must be a valid IANA timezone");

export const topicLabelSchema = nonblankText(160);
export type TopicLabel = z.infer<typeof topicLabelSchema>;

export const courseDefinitionSchema = z
  .object({
    id: z.uuid(),
    title: nonblankText(160),
    position: z.number().int().positive(),
  })
  .strict();
export type CourseDefinition = z.infer<typeof courseDefinitionSchema>;

export const unitDefinitionSchema = z
  .object({
    id: z.uuid(),
    courseId: z.uuid(),
    title: nonblankText(240),
    position: z.number().int().positive(),
  })
  .strict();
export type UnitDefinition = z.infer<typeof unitDefinitionSchema>;

export const assignmentDefinitionSchema = z
  .object({
    id: z.uuid(),
    courseId: z.uuid(),
    unitId: z.uuid().optional(),
    title: nonblankText(240),
    position: z.number().int().positive(),
    completionCondition: nonblankText(2_000),
    submissionEvidenceRequired: z.boolean(),
    dueAt: z.iso.datetime({ offset: true }).optional(),
  })
  .strict();
export type AssignmentDefinition = z.infer<typeof assignmentDefinitionSchema>;

export const resourceDefinitionSchema = z
  .object({
    id: z.uuid(),
    assignmentId: z.uuid(),
    label: nonblankText(160),
    url: httpUrlSchema.optional(),
    description: nonblankText(2_000).optional(),
  })
  .strict();
export type ResourceDefinition = z.infer<typeof resourceDefinitionSchema>;

export const parentCandidateDetailsSchema = z
  .object({
    notes: nonblankText(4_000).optional(),
    checkpointMinutes: z.number().int().min(1).max(180).optional(),
    title: nonblankText(300).optional(),
    dueAt: z.iso.datetime({ offset: true }).optional(),
    sourceUrl: httpUrlSchema.optional(),
  })
  .strict();
export type ParentCandidateDetails = z.infer<
  typeof parentCandidateDetailsSchema
>;
export const parentCandidateDetailsSnapshotSchema = z
  .object({
    revision: z.number().int().positive(),
    updatedAt: z.iso.datetime({ offset: true }),
    values: parentCandidateDetailsSchema,
  })
  .strict();
export type ParentCandidateDetailsSnapshot = z.infer<
  typeof parentCandidateDetailsSnapshotSchema
>;
export const saveCandidateDetailsCommandSchema = z
  .object({
    requestId: z.uuid(),
    sourceItemKey: nonblankText(256),
    expectedRevision: z.number().int().nonnegative(),
    details: parentCandidateDetailsSchema,
  })
  .strict();
export type SaveCandidateDetailsCommand = z.infer<
  typeof saveCandidateDetailsCommandSchema
>;

export const canvasAssignmentCandidateInputSchema = z
  .object({
    sourceItemKey: nonblankText(256),
    sourceCourseKey: nonblankText(128),
    courseTitle: nonblankText(200),
    courseColor: z
      .string()
      .regex(/^#[0-9a-f]{6}$/i)
      .optional(),
    sourceItemType: z.enum(["assignment", "quiz", "discussion"]).optional(),
    title: nonblankText(300),
    dueAt: z.iso.datetime({ offset: true }).optional(),
    sourceUrl: httpUrlSchema.optional(),
    parentDetails: parentCandidateDetailsSnapshotSchema.optional(),
    tracking: z
      .object({
        queueItemId: z.uuid(),
        stepId: z.uuid(),
        status: z.enum([
          "not-started",
          "active",
          "paused",
          "blocked",
          "done",
          "deferred",
          "skipped",
          "replaced",
        ]),
      })
      .strict()
      .optional(),
  })
  .strict();
export type CanvasAssignmentCandidateInput = z.infer<
  typeof canvasAssignmentCandidateInputSchema
>;

export const assignmentCandidateImportCommandSchema = z
  .object({
    requestId: z.uuid(),
    candidates: z
      .array(
        canvasAssignmentCandidateInputSchema.omit({
          parentDetails: true,
          tracking: true,
        }),
      )
      .min(1)
      .max(1_000),
  })
  .strict();
export type AssignmentCandidateImportCommand = z.infer<
  typeof assignmentCandidateImportCommandSchema
>;

export const assignmentCandidateSnapshotSchema =
  canvasAssignmentCandidateInputSchema
    .extend({
      id: z.uuid(),
      importRevision: z.number().int().positive(),
      importedAt: z.iso.datetime({ offset: true }),
    })
    .strict();
export type AssignmentCandidateSnapshot = z.infer<
  typeof assignmentCandidateSnapshotSchema
>;

export const assignmentCandidateImportResultSchema = z
  .object({
    createdCount: z.number().int().nonnegative(),
    updatedCount: z.number().int().nonnegative(),
    totalCount: z.number().int().nonnegative(),
    receivedAt: z.iso.datetime({ offset: true }),
  })
  .strict();
export type AssignmentCandidateImportResult = z.infer<
  typeof assignmentCandidateImportResultSchema
>;

export const assignmentCandidateListSchema = z
  .object({ candidates: z.array(assignmentCandidateSnapshotSchema) })
  .strict();
export type AssignmentCandidateList = z.infer<
  typeof assignmentCandidateListSchema
>;

export const topicDefinitionSchema = z
  .object({
    id: z.uuid(),
    assignmentId: z.uuid(),
    label: topicLabelSchema,
  })
  .strict();
export type TopicDefinition = z.infer<typeof topicDefinitionSchema>;

const knownNonActionStepStarters = new Set([
  "a",
  "an",
  "assignment",
  "chapter",
  "homework",
  "lesson",
  "notes",
  "page",
  "pages",
  "problems",
  "project",
  "quiz",
  "test",
  "the",
  "topic",
  "unit",
  "video",
  "worksheet",
]);

export const stepDefinitionSchema = z
  .object({
    id: z.uuid(),
    assignmentId: z.uuid(),
    position: z.number().int().positive(),
    actionVerb: nonblankText(80).refine(
      (value) =>
        /^\p{L}+(?:[-'’]\p{L}+)*$/u.test(value) &&
        !knownNonActionStepStarters.has(value.toLocaleLowerCase()),
      "Enter one action word, such as Read, Solve, or Explain",
    ),
    instruction: nonblankText(1_000),
    completionCondition: nonblankText(2_000),
    required: z.boolean(),
    meaningfulLearning: z.boolean().optional(),
    resourceIds: z.array(z.uuid()),
    topicIds: z.array(z.uuid()),
  })
  .strict()
  .superRefine((value, context) => {
    const firstWord = value.instruction
      .trim()
      .match(/^\p{L}+(?:[-'’]\p{L}+)*/u)?.[0];
    if (
      firstWord?.toLocaleLowerCase() !== value.actionVerb.toLocaleLowerCase()
    ) {
      context.addIssue({
        code: "custom",
        message: "Step instruction must begin with the action verb",
        path: ["instruction"],
      });
    }
  });
export type StepDefinition = z.infer<typeof stepDefinitionSchema>;

export const queueItemPlanningSchema = z
  .object({
    mustFinish: z.boolean(),
    studentReorderEligible: z.boolean().optional(),
    minMinutes: z.number().int().positive(),
    maxMinutes: z.number().int().positive(),
  })
  .strict()
  .refine((value) => value.maxMinutes >= value.minMinutes, {
    message: "Maximum minutes must be at least minimum minutes",
    path: ["maxMinutes"],
  });
export type QueueItemPlanning = z.infer<typeof queueItemPlanningSchema>;

export const unavailableWindowSchema = z
  .object({
    label: nonblankText(160),
    startTime: localTimeSchema,
    endTime: localTimeSchema,
  })
  .strict()
  .refine((value) => value.startTime < value.endTime, {
    message: "Unavailable window must end after it starts",
    path: ["endTime"],
  });
export type UnavailableWindow = z.infer<typeof unavailableWindowSchema>;

export const queueItemSnapshotSchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    position: z.number().int().positive(),
    planning: queueItemPlanningSchema,
  })
  .strict();
export type QueueItemSnapshot = z.infer<typeof queueItemSnapshotSchema>;

const dailyQueueGraphSchema = z
  .object({
    localDate: z.iso.date(),
    timezone: timezoneSchema,
    startTime: localTimeSchema,
    endTime: localTimeSchema,
    unavailableWindows: z.array(unavailableWindowSchema).default([]),
    courses: z.array(courseDefinitionSchema),
    units: z.array(unitDefinitionSchema).optional(),
    assignments: z.array(assignmentDefinitionSchema),
    resources: z.array(resourceDefinitionSchema),
    topics: z.array(topicDefinitionSchema),
    steps: z.array(stepDefinitionSchema),
    deferredStepIds: z.array(z.uuid()).default([]),
    queueItems: z.array(queueItemSnapshotSchema),
  })
  .strict();
type DailyQueueGraph = z.infer<typeof dailyQueueGraphSchema>;

function validateUniqueIds(
  values: { id: string }[],
  collection: keyof DailyQueueGraph,
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  values.forEach(({ id }, index) => {
    if (seen.has(id)) {
      context.addIssue({
        code: "custom",
        message: `Duplicate ${collection} identifier`,
        path: [collection, index, "id"],
      });
    }
    seen.add(id);
  });
}

function validateContiguousPositions(
  values: { position: number }[],
  collection: "courses" | "units" | "assignments" | "steps" | "queueItems",
  context: z.RefinementCtx,
): void {
  const positions = values
    .map(({ position }) => position)
    .sort((a, b) => a - b);
  positions.forEach((position, index) => {
    if (position !== index + 1) {
      context.addIssue({
        code: "custom",
        message: `${collection} positions must be contiguous`,
        path: [collection],
      });
    }
  });
}

function validateDailyQueueGraph(
  value: DailyQueueGraph,
  context: z.RefinementCtx,
): void {
  const issue = (message: string, path: (string | number)[]) =>
    context.addIssue({ code: "custom", message, path });

  if (value.startTime >= value.endTime) {
    issue("Daily Queue must end after it starts", ["endTime"]);
  }

  for (const collection of [
    "courses",
    "units",
    "assignments",
    "resources",
    "topics",
    "steps",
    "queueItems",
  ] as const) {
    validateUniqueIds(value[collection] ?? [], collection, context);
  }
  validateContiguousPositions(value.courses, "courses", context);
  validateContiguousPositions(value.queueItems, "queueItems", context);

  const courseIds = new Set(value.courses.map(({ id }) => id));
  const unitsById = new Map((value.units ?? []).map((unit) => [unit.id, unit]));
  const assignmentsById = new Map(
    value.assignments.map((assignment) => [assignment.id, assignment]),
  );
  const resourcesById = new Map(
    value.resources.map((resource) => [resource.id, resource]),
  );
  const topicsById = new Map(value.topics.map((topic) => [topic.id, topic]));
  const stepsById = new Map(value.steps.map((step) => [step.id, step]));

  if (new Set(value.deferredStepIds).size !== value.deferredStepIds.length) {
    issue("A Step may be deferred only once", ["deferredStepIds"]);
  }
  value.deferredStepIds.forEach((stepId, index) => {
    if (!stepsById.has(stepId)) {
      issue("Deferred Step references an unknown Step", [
        "deferredStepIds",
        index,
      ]);
    }
  });

  value.assignments.forEach((assignment, index) => {
    if (!courseIds.has(assignment.courseId)) {
      issue("Assignment references an unknown Course", [
        "assignments",
        index,
        "courseId",
      ]);
    }
    if (assignment.unitId !== undefined) {
      const unit = unitsById.get(assignment.unitId);
      if (unit === undefined || unit.courseId !== assignment.courseId) {
        issue("Assignment Unit must belong to the same Course", [
          "assignments",
          index,
          "unitId",
        ]);
      }
    }
  });
  value.courses.forEach((course) => {
    const units = (value.units ?? []).filter(
      ({ courseId }) => courseId === course.id,
    );
    validateContiguousPositions(units, "units", context);
  });
  (value.units ?? []).forEach((unit, index) => {
    if (!courseIds.has(unit.courseId)) {
      issue("Unit references an unknown Course", ["units", index, "courseId"]);
    }
  });
  value.courses.forEach((course) => {
    validateContiguousPositions(
      value.assignments.filter(({ courseId }) => courseId === course.id),
      "assignments",
      context,
    );
  });

  value.resources.forEach((resource, index) => {
    if (!assignmentsById.has(resource.assignmentId)) {
      issue("Resource references an unknown Assignment", [
        "resources",
        index,
        "assignmentId",
      ]);
    }
  });
  value.topics.forEach((topic, index) => {
    if (!assignmentsById.has(topic.assignmentId)) {
      issue("Topic references an unknown Assignment", [
        "topics",
        index,
        "assignmentId",
      ]);
    }
  });

  value.steps.forEach((step, index) => {
    if (!assignmentsById.has(step.assignmentId)) {
      issue("Step references an unknown Assignment", [
        "steps",
        index,
        "assignmentId",
      ]);
    }
    if (new Set(step.resourceIds).size !== step.resourceIds.length) {
      issue("Step Resource relationships must be unique", [
        "steps",
        index,
        "resourceIds",
      ]);
    }
    if (new Set(step.topicIds).size !== step.topicIds.length) {
      issue("Step Topic relationships must be unique", [
        "steps",
        index,
        "topicIds",
      ]);
    }
    step.resourceIds.forEach((resourceId) => {
      const resource = resourcesById.get(resourceId);
      if (!resource || resource.assignmentId !== step.assignmentId) {
        issue("Step Resource must belong to the same Assignment", [
          "steps",
          index,
          "resourceIds",
        ]);
      }
    });
    step.topicIds.forEach((topicId) => {
      const topic = topicsById.get(topicId);
      if (!topic || topic.assignmentId !== step.assignmentId) {
        issue("Step Topic must belong to the same Assignment", [
          "steps",
          index,
          "topicIds",
        ]);
      }
    });
  });
  value.assignments.forEach((assignment) => {
    validateContiguousPositions(
      value.steps.filter(({ assignmentId }) => assignmentId === assignment.id),
      "steps",
      context,
    );
  });

  const queuedStepIds = new Set<string>();
  value.queueItems.forEach((item, index) => {
    if (!stepsById.has(item.stepId)) {
      issue("Queue Item references an unknown Step", [
        "queueItems",
        index,
        "stepId",
      ]);
    }
    if (queuedStepIds.has(item.stepId)) {
      issue("A Step may appear only once in a Daily Queue", [
        "queueItems",
        index,
        "stepId",
      ]);
    }
    queuedStepIds.add(item.stepId);
  });
  value.deferredStepIds.forEach((stepId, index) => {
    if (queuedStepIds.has(stepId)) {
      issue("A Step cannot be both scheduled and deferred", [
        "deferredStepIds",
        index,
      ]);
    }
  });
}

export const dailyQueueDraftInputSchema = dailyQueueGraphSchema.superRefine(
  validateDailyQueueGraph,
);
export type DailyQueueDraftInput = z.infer<typeof dailyQueueDraftInputSchema>;

export const dailyQueueStatusSchema = z.enum([
  "draft",
  "ready",
  "active",
  "closed",
]);
export type DailyQueueStatus = z.infer<typeof dailyQueueStatusSchema>;

export const queueRollupPartSchema = z
  .object({
    itemCount: z.number().int().nonnegative(),
    mustFinishCount: z.number().int().nonnegative(),
    minMinutes: z.number().int().nonnegative(),
    maxMinutes: z.number().int().nonnegative(),
  })
  .strict();
export type QueueRollupPart = z.infer<typeof queueRollupPartSchema>;

export const dailyQueueRollupSchema = z
  .object({
    required: queueRollupPartSchema,
    optional: queueRollupPartSchema,
    total: queueRollupPartSchema,
  })
  .strict();
export type DailyQueueRollup = z.infer<typeof dailyQueueRollupSchema>;

export const dailyQueueSnapshotSchema = dailyQueueGraphSchema
  .extend({
    id: z.uuid(),
    revision: z.number().int().positive(),
    status: dailyQueueStatusSchema,
    rollup: dailyQueueRollupSchema,
  })
  .strict()
  .superRefine((value, context) => {
    validateDailyQueueGraph(value, context);
    const issue = (message: string, path: (string | number)[]) =>
      context.addIssue({ code: "custom", message, path });
    const stepsById = new Map(value.steps.map((step) => [step.id, step]));

    const required = {
      itemCount: 0,
      mustFinishCount: 0,
      minMinutes: 0,
      maxMinutes: 0,
    };
    const optional = { ...required };
    const seenStepIds = new Set<string>();
    value.queueItems.forEach((item, index) => {
      if (item.position !== index + 1) {
        issue("Queue Item positions must be contiguous and ordered", [
          "queueItems",
          index,
          "position",
        ]);
      }
      const step = stepsById.get(item.stepId);
      if (!step) {
        issue("Queue Item references an unknown Step", [
          "queueItems",
          index,
          "stepId",
        ]);
        return;
      }
      if (seenStepIds.has(item.stepId)) {
        issue("A Step may appear only once in a Daily Queue", [
          "queueItems",
          index,
          "stepId",
        ]);
      }
      seenStepIds.add(item.stepId);
      const part = step.required ? required : optional;
      part.itemCount += 1;
      part.mustFinishCount += item.planning.mustFinish ? 1 : 0;
      part.minMinutes += item.planning.minMinutes;
      part.maxMinutes += item.planning.maxMinutes;
    });

    const total = {
      itemCount: required.itemCount + optional.itemCount,
      mustFinishCount: required.mustFinishCount + optional.mustFinishCount,
      minMinutes: required.minMinutes + optional.minMinutes,
      maxMinutes: required.maxMinutes + optional.maxMinutes,
    };
    const rollupParts = { required, optional, total };
    for (const partName of ["required", "optional", "total"] as const) {
      for (const field of [
        "itemCount",
        "mustFinishCount",
        "minMinutes",
        "maxMinutes",
      ] as const) {
        if (value.rollup[partName][field] !== rollupParts[partName][field]) {
          issue("Rollup must match its Queue Items", [
            "rollup",
            partName,
            field,
          ]);
        }
      }
    }
  });
export type DailyQueueSnapshot = z.infer<typeof dailyQueueSnapshotSchema>;

export const createDailyQueueCommandSchema = mutationEnvelopeSchema
  .extend({
    expectedRevision: z.literal(0),
    draft: dailyQueueDraftInputSchema,
  })
  .strict();
export type CreateDailyQueueCommand = z.infer<
  typeof createDailyQueueCommandSchema
>;

const existingDailyQueueCommandEnvelopeSchema = mutationEnvelopeSchema.extend({
  expectedRevision: z.number().int().positive(),
  queueId: z.uuid(),
});

export const updateDailyQueueCommandSchema =
  existingDailyQueueCommandEnvelopeSchema
    .extend({ draft: dailyQueueDraftInputSchema })
    .strict();
export type UpdateDailyQueueCommand = z.infer<
  typeof updateDailyQueueCommandSchema
>;

export const markDailyQueueReadyCommandSchema =
  existingDailyQueueCommandEnvelopeSchema.strict();
export type MarkDailyQueueReadyCommand = z.infer<
  typeof markDailyQueueReadyCommandSchema
>;

export const activateDailyQueueCommandSchema =
  existingDailyQueueCommandEnvelopeSchema.strict();
export type ActivateDailyQueueCommand = z.infer<
  typeof activateDailyQueueCommandSchema
>;

export const studentQueueReorderCommandSchema =
  existingDailyQueueCommandEnvelopeSchema
    .extend({
      intent: z.literal("reorder"),
      orderedQueueItemIds: z
        .array(z.uuid())
        .min(1)
        .refine(
          (ids) => new Set(ids).size === ids.length,
          "Queue Item identifiers must be unique",
        ),
    })
    .strict();
export type StudentQueueReorderCommand = z.infer<
  typeof studentQueueReorderCommandSchema
>;

export const validationIssueSchema = z
  .object({
    path: z.array(z.union([z.string(), z.number().int().nonnegative()])),
    message: nonblankText(300),
  })
  .strict();
export type ValidationIssue = z.infer<typeof validationIssueSchema>;

export const dailyQueueAcceptedResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    acceptedRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    publicationSequence: z.number().int().positive(),
    snapshot: dailyQueueSnapshotSchema,
  })
  .strict()
  .refine(
    (value) => value.acceptedRevision === value.snapshot.revision,
    "Accepted revision must match the Daily Queue snapshot",
  );
export type DailyQueueAcceptedResult = z.infer<
  typeof dailyQueueAcceptedResultSchema
>;

export const dailyQueueValidationFailureSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.literal("VALIDATION_FAILED"),
        issues: z.array(validationIssueSchema).min(1),
      })
      .strict(),
  })
  .strict();
export type DailyQueueValidationFailure = z.infer<
  typeof dailyQueueValidationFailureSchema
>;

export const dailyQueueRevisionConflictSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.literal("REVISION_CONFLICT"),
        currentRevision: z.number().int().positive(),
        changedBy: roleSchema,
        changedAt: z.iso.datetime({ offset: true }),
        snapshot: dailyQueueSnapshotSchema,
      })
      .strict()
      .refine(
        (value) => value.currentRevision === value.snapshot.revision,
        "Current revision must match the Daily Queue snapshot",
      ),
  })
  .strict();
export type DailyQueueRevisionConflict = z.infer<
  typeof dailyQueueRevisionConflictSchema
>;

export const dailyQueueCommandErrorSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.enum([
          "NOT_AUTHORIZED",
          "STATE_TRANSITION_INVALID",
          "OFFLINE_OR_UNAVAILABLE",
        ]),
        message: nonblankText(300),
        allowedNextActions: z.array(nonblankText(100)).optional(),
      })
      .strict(),
  })
  .strict();
export type DailyQueueCommandError = z.infer<
  typeof dailyQueueCommandErrorSchema
>;

export const dailyQueueCommandResultSchema = z.union([
  dailyQueueAcceptedResultSchema,
  dailyQueueValidationFailureSchema,
  dailyQueueRevisionConflictSchema,
  dailyQueueCommandErrorSchema,
]);
export type DailyQueueCommandResult = z.infer<
  typeof dailyQueueCommandResultSchema
>;

export const dailyQueueChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("DailyQueueChanged"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: dailyQueueSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must match the Daily Queue snapshot",
        path: ["resultingRevision"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must follow the base revision",
        path: ["baseRevision"],
      });
    }
  });
export type DailyQueueChangedEvent = z.infer<
  typeof dailyQueueChangedEventSchema
>;

export const studentWorkStateSnapshotSchema = z
  .object({
    revision: z.number().int().positive(),
    activeQueueId: z.uuid().nullable(),
    activeQueueRevision: z.number().int().positive().nullable(),
    activeStepId: z.uuid().nullable(),
    activeQueueItemId: z.uuid().nullable(),
    changedBy: roleSchema.nullable(),
    changedAt: z.iso.datetime({ offset: true }),
  })
  .strict()
  .superRefine((value, context) => {
    const hasActiveQueue = value.activeQueueId !== null;
    if (hasActiveQueue !== (value.activeQueueRevision !== null)) {
      context.addIssue({
        code: "custom",
        message:
          "Active Daily Queue identity and revision must appear together",
        path: ["activeQueueRevision"],
      });
    }

    const hasActiveStep = value.activeStepId !== null;
    if (hasActiveStep !== (value.activeQueueItemId !== null)) {
      context.addIssue({
        code: "custom",
        message: "Active Step and Queue Item identity must appear together",
        path: ["activeQueueItemId"],
      });
    }
    if (hasActiveStep && !hasActiveQueue) {
      context.addIssue({
        code: "custom",
        message: "An Active Step requires an Active Daily Queue",
        path: ["activeStepId"],
      });
    }
  });
export type StudentWorkStateSnapshot = z.infer<
  typeof studentWorkStateSnapshotSchema
>;

export const startActiveStepCommandSchema = mutationEnvelopeSchema
  .extend({
    queueId: z.uuid(),
    queueItemId: z.uuid(),
    intent: z.enum(["start", "switch"]),
  })
  .strict();
export type StartActiveStepCommand = z.infer<
  typeof startActiveStepCommandSchema
>;

export const startActiveStepAcceptedResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    acceptedRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    publicationSequence: z.number().int().positive(),
    snapshot: studentWorkStateSnapshotSchema,
  })
  .strict()
  .refine(
    (value) => value.acceptedRevision === value.snapshot.revision,
    "Accepted revision must match the Student Work State snapshot",
  );
export type StartActiveStepAcceptedResult = z.infer<
  typeof startActiveStepAcceptedResultSchema
>;

export const startActiveStepValidationFailureSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.literal("VALIDATION_FAILED"),
        issues: z.array(validationIssueSchema).min(1),
      })
      .strict(),
  })
  .strict();
export type StartActiveStepValidationFailure = z.infer<
  typeof startActiveStepValidationFailureSchema
>;

const authoritativeActiveStepSchema = z
  .object({
    stepId: z.uuid(),
    queueItemId: z.uuid(),
  })
  .strict();

export const startActiveStepRevisionConflictSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.literal("REVISION_CONFLICT"),
        currentRevision: z.number().int().positive(),
        changedBy: roleSchema,
        changedAt: z.iso.datetime({ offset: true }),
        authoritativeWinner: authoritativeActiveStepSchema.nullable(),
        snapshot: studentWorkStateSnapshotSchema,
      })
      .strict()
      .superRefine((value, context) => {
        if (value.currentRevision !== value.snapshot.revision) {
          context.addIssue({
            code: "custom",
            message:
              "Current revision must match the Student Work State snapshot",
            path: ["currentRevision"],
          });
        }

        const winnerMatchesSnapshot =
          value.authoritativeWinner === null
            ? value.snapshot.activeStepId === null &&
              value.snapshot.activeQueueItemId === null
            : value.authoritativeWinner.stepId ===
                value.snapshot.activeStepId &&
              value.authoritativeWinner.queueItemId ===
                value.snapshot.activeQueueItemId;
        if (!winnerMatchesSnapshot) {
          context.addIssue({
            code: "custom",
            message: "Authoritative winner must match the current Active Step",
            path: ["authoritativeWinner"],
          });
        }
      }),
  })
  .strict();
export type StartActiveStepRevisionConflict = z.infer<
  typeof startActiveStepRevisionConflictSchema
>;

export const startActiveStepCommandErrorSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.enum([
          "NOT_AUTHORIZED",
          "STATE_TRANSITION_INVALID",
          "REQUEST_ID_REUSED",
          "OFFLINE_OR_UNAVAILABLE",
        ]),
        message: nonblankText(300),
        allowedNextActions: z.array(nonblankText(100)).optional(),
      })
      .strict(),
  })
  .strict();
export type StartActiveStepCommandError = z.infer<
  typeof startActiveStepCommandErrorSchema
>;

export const startActiveStepCommandResultSchema = z.union([
  startActiveStepAcceptedResultSchema,
  startActiveStepValidationFailureSchema,
  startActiveStepRevisionConflictSchema,
  startActiveStepCommandErrorSchema,
]);
export type StartActiveStepCommandResult = z.infer<
  typeof startActiveStepCommandResultSchema
>;

export const activeStepChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("ActiveStepChanged"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: studentWorkStateSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message:
          "Resulting revision must match the Student Work State snapshot",
        path: ["resultingRevision"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must follow the base revision",
        path: ["baseRevision"],
      });
    }
  });
export type ActiveStepChangedEvent = z.infer<
  typeof activeStepChangedEventSchema
>;

export const focusExtensionMinutesSchema = z.union([
  z.literal(5),
  z.literal(10),
  z.literal(15),
]);
export type FocusExtensionMinutes = z.infer<typeof focusExtensionMinutesSchema>;

const focusBlockEventBaseSchema = z.object({
  eventId: z.uuid(),
  requestId: z.uuid(),
  sequence: z.number().int().positive(),
  serverTime: z.iso.datetime({ offset: true }),
  actor: z.object({ role: roleSchema }).strict(),
  occurredAtClient: z.iso.datetime({ offset: true }).optional(),
});

const focusGapCorrectionSchema = z
  .object({
    correctedStopAt: z.iso.datetime({ offset: true }),
    correctedResumeAt: z.iso.datetime({ offset: true }),
  })
  .strict()
  .refine(
    (value) =>
      Date.parse(value.correctedStopAt) <= Date.parse(value.correctedResumeAt),
    {
      message: "Corrected resume must not precede corrected stop",
      path: ["correctedResumeAt"],
    },
  );

export const focusBlockEventSchema = z.discriminatedUnion("type", [
  focusBlockEventBaseSchema.extend({ type: z.literal("Start") }).strict(),
  focusBlockEventBaseSchema.extend({ type: z.literal("Pause") }).strict(),
  focusBlockEventBaseSchema.extend({ type: z.literal("Resume") }).strict(),
  focusBlockEventBaseSchema
    .extend({
      type: z.literal("Extend"),
      extensionMinutes: focusExtensionMinutesSchema,
    })
    .strict(),
  focusBlockEventBaseSchema.extend({ type: z.literal("End") }).strict(),
  focusBlockEventBaseSchema
    .extend({
      type: z.literal("ReconcileGap"),
      resolution: z.enum(["keep", "correct", "unsure"]),
      runningIntervalStartedAt: z.iso.datetime({ offset: true }),
      runningIntervalEndedAt: z.iso.datetime({ offset: true }),
      originalTimerRunningDurationMs: z.number().int().nonnegative(),
      acceptedTimerRunningDurationMs: z.number().int().nonnegative(),
      correction: focusGapCorrectionSchema.nullable(),
    })
    .strict()
    .superRefine((value, context) => {
      const intervalStartedAtMs = Date.parse(value.runningIntervalStartedAt);
      const intervalEndedAtMs = Date.parse(value.runningIntervalEndedAt);
      if (intervalStartedAtMs >= intervalEndedAtMs) {
        context.addIssue({
          code: "custom",
          message: "The timer-running interval must end after it starts",
          path: ["runningIntervalEndedAt"],
        });
      }
      const correctionRequired = value.resolution === "correct";
      if (correctionRequired !== (value.correction !== null)) {
        context.addIssue({
          code: "custom",
          message: "Only a corrected gap has corrected stop and resume times",
          path: ["correction"],
        });
      }
      if (
        value.correction !== null &&
        (Date.parse(value.correction.correctedStopAt) < intervalStartedAtMs ||
          Date.parse(value.correction.correctedResumeAt) > intervalEndedAtMs)
      ) {
        context.addIssue({
          code: "custom",
          message: "Corrected times must stay inside the factual running gap",
          path: ["correction"],
        });
      }
      if (
        value.acceptedTimerRunningDurationMs >
        value.originalTimerRunningDurationMs
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Accepted timer-running duration cannot exceed the factual interval",
          path: ["acceptedTimerRunningDurationMs"],
        });
      }
      if (
        !correctionRequired &&
        value.acceptedTimerRunningDurationMs !==
          value.originalTimerRunningDurationMs
      ) {
        context.addIssue({
          code: "custom",
          message: "Only a correction may adjust timer-running duration",
          path: ["acceptedTimerRunningDurationMs"],
        });
      }
      const factualDurationMs = intervalEndedAtMs - intervalStartedAtMs;
      if (value.originalTimerRunningDurationMs !== factualDurationMs) {
        context.addIssue({
          code: "custom",
          message:
            "Original timer-running duration must match the factual interval",
          path: ["originalTimerRunningDurationMs"],
        });
      }
      if (value.correction !== null) {
        const correctedDurationMs =
          Date.parse(value.correction.correctedStopAt) -
          intervalStartedAtMs +
          (intervalEndedAtMs - Date.parse(value.correction.correctedResumeAt));
        if (value.acceptedTimerRunningDurationMs !== correctedDurationMs) {
          context.addIssue({
            code: "custom",
            message:
              "Accepted timer-running duration must match the explicit correction",
            path: ["acceptedTimerRunningDurationMs"],
          });
        }
      }
    }),
]);
export type FocusBlockEvent = z.infer<typeof focusBlockEventSchema>;

export const focusBlockSnapshotSchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    revision: z.number().int().positive(),
    state: z.enum(["running", "paused", "ended"]),
    plannedMinimumMinutes: z.number().int().positive(),
    plannedMaximumMinutes: z.number().int().positive(),
    plannedExtensionMinutes: z.number().int().nonnegative(),
    accumulatedTimerRunningDurationMs: z.number().int().nonnegative(),
    timerRunningDurationMs: z.number().int().nonnegative(),
    currentSegmentStartedAt: z.iso.datetime({ offset: true }).nullable(),
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }).nullable(),
    observedAt: z.iso.datetime({ offset: true }),
    suspiciousGap: z
      .object({
        status: z.literal("reconciliation-required"),
        runningIntervalStartedAt: z.iso.datetime({ offset: true }),
        detectedAt: z.iso.datetime({ offset: true }),
        timerRunningDurationMs: z.number().int().positive(),
      })
      .strict()
      .nullable(),
    events: z.array(focusBlockEventSchema).min(1),
  })
  .strict()
  .superRefine((value, context) => {
    const issue = (message: string, path: (string | number)[]) =>
      context.addIssue({ code: "custom", message, path });
    if (value.plannedMaximumMinutes < value.plannedMinimumMinutes) {
      issue("Planned maximum must be at least the planned minimum", [
        "plannedMaximumMinutes",
      ]);
    }
    if (value.revision !== value.events.length) {
      issue("Focus Block revision must match its immutable event ledger", [
        "revision",
      ]);
    }
    if (value.events[0]?.type !== "Start") {
      issue("A Focus Block event ledger must begin with Start", ["events", 0]);
    }
    const eventIds = new Set<string>();
    const requestIds = new Set<string>();
    value.events.forEach((event, index) => {
      if (eventIds.has(event.eventId)) {
        issue("Focus Block event identifiers must be unique", [
          "events",
          index,
          "eventId",
        ]);
      }
      eventIds.add(event.eventId);
      if (requestIds.has(event.requestId)) {
        issue("A duplicate request cannot append another Focus Block event", [
          "events",
          index,
          "requestId",
        ]);
      }
      requestIds.add(event.requestId);
      if (event.sequence !== index + 1) {
        issue("Focus Block event sequence must be contiguous", [
          "events",
          index,
          "sequence",
        ]);
      }
      const previous = value.events[index - 1];
      if (
        previous !== undefined &&
        Date.parse(event.serverTime) < Date.parse(previous.serverTime)
      ) {
        issue("Focus Block events must be in canonical timestamp order", [
          "events",
          index,
          "serverTime",
        ]);
      }
    });
    const extensionMinutes = value.events.reduce(
      (total, event) =>
        total + (event.type === "Extend" ? event.extensionMinutes : 0),
      0,
    );
    if (extensionMinutes !== value.plannedExtensionMinutes) {
      issue("Planned extensions must match accepted Extend events", [
        "plannedExtensionMinutes",
      ]);
    }
    const isRunning = value.state === "running";
    if (isRunning !== (value.currentSegmentStartedAt !== null)) {
      issue("Only a Running Focus Block has an open canonical segment", [
        "currentSegmentStartedAt",
      ]);
    }
    const isEnded = value.state === "ended";
    if (isEnded !== (value.endedAt !== null)) {
      issue("Only an Ended Focus Block has an end timestamp", ["endedAt"]);
    }
    if (
      value.timerRunningDurationMs < value.accumulatedTimerRunningDurationMs
    ) {
      issue(
        "Observed timer-running duration cannot be below accumulated duration",
        ["timerRunningDurationMs"],
      );
    }
    if (
      !isRunning &&
      value.timerRunningDurationMs !== value.accumulatedTimerRunningDurationMs
    ) {
      issue("A closed timer segment cannot add projected running duration", [
        "timerRunningDurationMs",
      ]);
    }
    if (
      value.currentSegmentStartedAt !== null &&
      Date.parse(value.currentSegmentStartedAt) > Date.parse(value.observedAt)
    ) {
      issue("Observation cannot precede the open canonical segment", [
        "observedAt",
      ]);
    }
  });
export type FocusBlockSnapshot = z.infer<typeof focusBlockSnapshotSchema>;

const focusCommandBaseSchema = mutationEnvelopeSchema.extend({
  focusBlockId: z.uuid(),
  occurredAtClient: z.iso.datetime({ offset: true }).optional(),
});

export const startFocusBlockCommandSchema = focusCommandBaseSchema
  .extend({
    intent: z.literal("start"),
    stepId: z.uuid(),
    plannedMinimumMinutes: z.number().int().positive(),
    plannedMaximumMinutes: z.number().int().positive(),
  })
  .strict()
  .refine((value) => value.expectedRevision === 0, {
    message: "A new Focus Block starts from revision zero",
    path: ["expectedRevision"],
  })
  .refine(
    (value) => value.plannedMaximumMinutes >= value.plannedMinimumMinutes,
    {
      message: "Planned maximum must be at least the planned minimum",
      path: ["plannedMaximumMinutes"],
    },
  );

const existingFocusCommandBaseSchema = focusCommandBaseSchema.extend({
  expectedRevision: z.number().int().positive(),
});

export const pauseFocusBlockCommandSchema = existingFocusCommandBaseSchema
  .extend({ intent: z.literal("pause") })
  .strict();
export const resumeFocusBlockCommandSchema = existingFocusCommandBaseSchema
  .extend({ intent: z.literal("resume") })
  .strict();
export const extendFocusBlockCommandSchema = existingFocusCommandBaseSchema
  .extend({
    intent: z.literal("extend"),
    extensionMinutes: focusExtensionMinutesSchema,
  })
  .strict();
export const endFocusBlockCommandSchema = existingFocusCommandBaseSchema
  .extend({ intent: z.literal("end") })
  .strict();
export const reconcileFocusGapCommandSchema = existingFocusCommandBaseSchema
  .extend({
    intent: z.literal("reconcile-gap"),
    resolution: z.enum(["keep", "correct", "unsure"]),
    correction: focusGapCorrectionSchema.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const hasCorrection = value.correction !== undefined;
    if (value.resolution === "correct" && !hasCorrection) {
      context.addIssue({
        code: "custom",
        message: "A correction requires corrected stop and resume times",
        path: ["correction"],
      });
    }
    if (value.resolution !== "correct" && hasCorrection) {
      context.addIssue({
        code: "custom",
        message: "Keep or unsure cannot change stop and resume times",
        path: ["correction"],
      });
    }
  });

export const focusCommandSchema = z.union([
  startFocusBlockCommandSchema,
  pauseFocusBlockCommandSchema,
  resumeFocusBlockCommandSchema,
  extendFocusBlockCommandSchema,
  endFocusBlockCommandSchema,
  reconcileFocusGapCommandSchema,
]);
export type FocusCommand = z.infer<typeof focusCommandSchema>;

export const focusCommandAcceptedResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    acceptedRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    publicationSequence: z.number().int().positive(),
    snapshot: focusBlockSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.acceptedRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Accepted revision must match the Focus Block snapshot",
        path: ["acceptedRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.observedAt) {
      context.addIssue({
        code: "custom",
        message: "Server time must anchor the Focus Block observation",
        path: ["serverTime"],
      });
    }
  });

export const focusCommandRevisionConflictSchema = z
  .object({
    status: z.literal("rejected"),
    error: z
      .object({
        code: z.literal("REVISION_CONFLICT"),
        currentRevision: z.number().int().nonnegative(),
        changedBy: roleSchema,
        changedAt: z.iso.datetime({ offset: true }),
        snapshot: focusBlockSnapshotSchema.nullable(),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.error.currentRevision !== (value.error.snapshot?.revision ?? 0)) {
      context.addIssue({
        code: "custom",
        message: "Current revision must match the Focus Block snapshot",
        path: ["error", "currentRevision"],
      });
    }
  });

export const focusCommandResultSchema = z.union([
  focusCommandAcceptedResultSchema,
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("VALIDATION_FAILED"),
          issues: z.array(validationIssueSchema).min(1),
        })
        .strict(),
    })
    .strict(),
  focusCommandRevisionConflictSchema,
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("STATE_TRANSITION_INVALID"),
          message: nonblankText(500),
          currentState: z.enum(["missing", "running", "paused", "ended"]),
          allowedNextIntents: z.array(
            z.enum([
              "start",
              "pause",
              "resume",
              "extend",
              "end",
              "reconcile-gap",
            ]),
          ),
        })
        .strict(),
    })
    .strict(),
]);
export type FocusCommandResult = z.infer<typeof focusCommandResultSchema>;

export const focusBlockChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("FocusBlockChanged"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: focusBlockSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must match the Focus Block snapshot",
        path: ["resultingRevision"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must follow the base revision",
        path: ["baseRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.observedAt) {
      context.addIssue({
        code: "custom",
        message: "Event server time must anchor its Focus Block snapshot",
        path: ["serverTime"],
      });
    }
  });
export type FocusBlockChangedEvent = z.infer<
  typeof focusBlockChangedEventSchema
>;

export const breakMinutesSchema = z.union([z.literal(5), z.literal(10)]);
export type BreakMinutes = z.infer<typeof breakMinutesSchema>;

export const recoveryBarrierSchema = z.enum([
  "unclear-directions",
  "step-too-large",
  "technical-issue",
  "distraction",
  "fatigue-unwell",
  "waiting-for-help",
  "other",
]);
export type RecoveryBarrier = z.infer<typeof recoveryBarrierSchema>;

export const recoveryActionSchema = z.enum([
  "reread-directions",
  "work-one-small-part",
  "retry-resource",
  "restart-for-five",
  "take-five-minute-break",
  "request-help",
  "continue-active-step",
]);
export type RecoveryAction = z.infer<typeof recoveryActionSchema>;

export const recoveryActionForBarrier: Record<RecoveryBarrier, RecoveryAction> =
  {
    "unclear-directions": "reread-directions",
    "step-too-large": "work-one-small-part",
    "technical-issue": "retry-resource",
    distraction: "restart-for-five",
    "fatigue-unwell": "take-five-minute-break",
    "waiting-for-help": "request-help",
    other: "continue-active-step",
  };

export const breakSnapshotSchema = z
  .object({
    id: z.uuid(),
    revision: z.number().int().positive(),
    state: z.enum(["running", "ended"]),
    returnStepId: z.uuid(),
    plannedMinutes: breakMinutesSchema,
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }).nullable(),
    observedAt: z.iso.datetime({ offset: true }),
    breakDurationMs: z.number().int().nonnegative(),
  })
  .strict()
  .superRefine((value, context) => {
    const end = value.endedAt ?? value.observedAt;
    if ((value.state === "ended") !== (value.endedAt !== null)) {
      context.addIssue({
        code: "custom",
        message: "Only an Ended Break has an end timestamp",
        path: ["endedAt"],
      });
    }
    if (Date.parse(end) < Date.parse(value.startedAt)) {
      context.addIssue({
        code: "custom",
        message: "Break observation cannot precede its start",
        path: ["observedAt"],
      });
    }
    if (
      value.breakDurationMs !==
      Date.parse(end) - Date.parse(value.startedAt)
    ) {
      context.addIssue({
        code: "custom",
        message: "Break duration must derive from canonical timestamps",
        path: ["breakDurationMs"],
      });
    }
  });
export type BreakSnapshot = z.infer<typeof breakSnapshotSchema>;

export const transitionSnapshotSchema = z
  .object({
    id: z.uuid(),
    revision: z.number().int().positive(),
    state: z.enum(["running", "ended"]),
    source: z.enum(["break", "focus-block", "stuck"]),
    sourceId: z.uuid().nullable(),
    returnStepId: z.uuid(),
    startedAt: z.iso.datetime({ offset: true }),
    endedAt: z.iso.datetime({ offset: true }).nullable(),
    observedAt: z.iso.datetime({ offset: true }),
    transitionDurationMs: z.number().int().nonnegative(),
    recoveryDueAt: z.iso.datetime({ offset: true }),
    recoveryDue: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    const end = value.endedAt ?? value.observedAt;
    const started = Date.parse(value.startedAt);
    const ended = Date.parse(end);
    if ((value.state === "ended") !== (value.endedAt !== null)) {
      context.addIssue({
        code: "custom",
        message: "Only an Ended transition has an end timestamp",
        path: ["endedAt"],
      });
    }
    if (ended < started) {
      context.addIssue({
        code: "custom",
        message: "Transition observation cannot precede its start",
        path: ["observedAt"],
      });
    }
    if (value.transitionDurationMs !== ended - started) {
      context.addIssue({
        code: "custom",
        message: "Transition duration must derive from canonical timestamps",
        path: ["transitionDurationMs"],
      });
    }
    const due = ended >= Date.parse(value.recoveryDueAt);
    if (value.recoveryDue !== due) {
      context.addIssue({
        code: "custom",
        message: "Recovery due state must derive from the canonical threshold",
        path: ["recoveryDue"],
      });
    }
  });
export type TransitionSnapshot = z.infer<typeof transitionSnapshotSchema>;

export const recoveryCheckInSnapshotSchema = z
  .object({
    id: z.uuid(),
    transitionId: z.uuid(),
    barrier: recoveryBarrierSchema,
    action: recoveryActionSchema,
    actor: z.object({ role: roleSchema }).strict(),
    recordedAt: z.iso.datetime({ offset: true }),
  })
  .strict()
  .superRefine((value, context) => {
    if (recoveryActionForBarrier[value.barrier] !== value.action) {
      context.addIssue({
        code: "custom",
        message: "Choose the approved concrete next action for this barrier",
        path: ["action"],
      });
    }
  });
export type RecoveryCheckInSnapshot = z.infer<
  typeof recoveryCheckInSnapshotSchema
>;

export const parkingLotEntrySnapshotSchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    text: nonblankText(500),
    createdAt: z.iso.datetime({ offset: true }),
    resolvedAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();
export type ParkingLotEntrySnapshot = z.infer<
  typeof parkingLotEntrySnapshotSchema
>;

export const studentRecoverySnapshotSchema = z
  .object({
    revision: z.number().int().positive(),
    observedAt: z.iso.datetime({ offset: true }),
    break: breakSnapshotSchema.nullable(),
    transition: transitionSnapshotSchema.nullable(),
    latestRecovery: recoveryCheckInSnapshotSchema.nullable(),
    parkingLotEntries: z.array(parkingLotEntrySnapshotSchema),
  })
  .strict();
export type StudentRecoverySnapshot = z.infer<
  typeof studentRecoverySnapshotSchema
>;

export const studentRecoveryEventSnapshotSchema = studentRecoverySnapshotSchema
  .omit({ parkingLotEntries: true })
  .extend({
    parkingLotEntries: z.array(
      parkingLotEntrySnapshotSchema.omit({ text: true }).strict(),
    ),
  })
  .strict();
export type StudentRecoveryEventSnapshot = z.infer<
  typeof studentRecoveryEventSnapshotSchema
>;

const recoveryCommandBaseSchema = mutationEnvelopeSchema.extend({
  expectedRevision: z.number().int().positive(),
});
export const startBreakCommandSchema = recoveryCommandBaseSchema
  .extend({
    intent: z.literal("start-break"),
    breakId: z.uuid(),
    plannedMinutes: breakMinutesSchema,
  })
  .strict();
export const endBreakCommandSchema = recoveryCommandBaseSchema
  .extend({ intent: z.literal("end-break"), breakId: z.uuid() })
  .strict();
export const requestRecoveryCommandSchema = recoveryCommandBaseSchema
  .extend({ intent: z.literal("im-stuck"), transitionId: z.uuid() })
  .strict();
export const returnToStepCommandSchema = recoveryCommandBaseSchema
  .extend({ intent: z.literal("return-to-step"), transitionId: z.uuid() })
  .strict();
export const recordRecoveryCommandSchema = recoveryCommandBaseSchema
  .extend({
    intent: z.literal("record-recovery"),
    transitionId: z.uuid(),
    barrier: recoveryBarrierSchema,
    action: recoveryActionSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (recoveryActionForBarrier[value.barrier] !== value.action) {
      context.addIssue({
        code: "custom",
        message: "Choose the approved concrete next action for this barrier",
        path: ["action"],
      });
    }
  });
export const parkThoughtCommandSchema = recoveryCommandBaseSchema
  .extend({
    intent: z.literal("park-thought"),
    entryId: z.uuid(),
    text: nonblankText(500),
  })
  .strict();

export const recoveryCommandSchema = z.union([
  startBreakCommandSchema,
  endBreakCommandSchema,
  requestRecoveryCommandSchema,
  returnToStepCommandSchema,
  recordRecoveryCommandSchema,
  parkThoughtCommandSchema,
]);
export type RecoveryCommand = z.infer<typeof recoveryCommandSchema>;

export const recoveryCommandAcceptedResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    acceptedRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    publicationSequence: z.number().int().positive(),
    snapshot: studentRecoverySnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.acceptedRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Accepted revision must match the recovery snapshot",
        path: ["acceptedRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.observedAt) {
      context.addIssue({
        code: "custom",
        message: "Server time must anchor the recovery snapshot",
        path: ["serverTime"],
      });
    }
  });
export type RecoveryCommandAcceptedResult = z.infer<
  typeof recoveryCommandAcceptedResultSchema
>;

export const recoveryCommandResultSchema = z.union([
  recoveryCommandAcceptedResultSchema,
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("VALIDATION_FAILED"),
          issues: z.array(validationIssueSchema).min(1),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("REVISION_CONFLICT"),
          currentRevision: z.number().int().positive(),
          changedBy: roleSchema,
          changedAt: z.iso.datetime({ offset: true }),
          snapshot: studentRecoverySnapshotSchema,
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.enum(["STATE_TRANSITION_INVALID", "RECOVERY_NOT_DUE"]),
          message: nonblankText(500),
          allowedNextIntents: z.array(
            z.enum([
              "start-break",
              "end-break",
              "im-stuck",
              "return-to-step",
              "record-recovery",
              "park-thought",
            ]),
          ),
        })
        .strict(),
    })
    .strict(),
]);
export type RecoveryCommandResult = z.infer<typeof recoveryCommandResultSchema>;

export const studentRecoveryChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: z.enum([
      "BreakChanged",
      "TransitionChanged",
      "RecoveryRecorded",
      "ParkingLotChanged",
    ]),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: studentRecoveryEventSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must match the recovery snapshot",
        path: ["resultingRevision"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must follow the base revision",
        path: ["baseRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.observedAt) {
      context.addIssue({
        code: "custom",
        message: "Event server time must anchor the recovery snapshot",
        path: ["serverTime"],
      });
    }
  });
export type StudentRecoveryChangedEvent = z.infer<
  typeof studentRecoveryChangedEventSchema
>;

export const stepOutcomeStatusSchema = z.enum([
  "not-started",
  "active",
  "paused",
  "blocked",
  "done",
  "deferred",
  "skipped",
]);
export type StepOutcomeStatus = z.infer<typeof stepOutcomeStatusSchema>;

export const assignmentOutcomeStatusSchema = z.enum([
  "unplanned",
  "planned",
  "in-progress",
  "ready-to-verify",
  "completed",
  "deferred",
  "cancelled",
]);
export type AssignmentOutcomeStatus = z.infer<
  typeof assignmentOutcomeStatusSchema
>;

export const queueItemStatusAtCloseSchema = z
  .object({
    step: stepOutcomeStatusSchema,
    assignment: assignmentOutcomeStatusSchema,
  })
  .strict();
export type QueueItemStatusAtClose = z.infer<
  typeof queueItemStatusAtCloseSchema
>;

export const closeDayReviewDispositionSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("replan"),
      targetLocalDate: z.iso.date(),
      carryoverPosition: z.number().int().positive(),
    })
    .strict(),
  z.object({ kind: z.literal("defer") }).strict(),
  z.object({ kind: z.literal("retain") }).strict(),
  z.object({ kind: z.literal("parent-cancelled") }).strict(),
]);
export type CloseDayReviewDisposition = z.infer<
  typeof closeDayReviewDispositionSchema
>;

export const closeDayReviewItemSchema = z
  .object({
    queueItemId: z.uuid(),
    stepId: z.uuid(),
    assignmentId: z.uuid(),
    mustFinish: z.boolean(),
    statusAtClose: queueItemStatusAtCloseSchema,
    disposition: closeDayReviewDispositionSchema,
  })
  .strict()
  .refine(({ statusAtClose }) => statusAtClose.step !== "done", {
    message: "Only unfinished Queue Items belong in close-day review scope",
    path: ["statusAtClose", "step"],
  });
export type CloseDayReviewItem = z.infer<typeof closeDayReviewItemSchema>;

export const closeDayProposedNextStepSchema = z
  .object({
    stepId: z.uuid(),
    targetLocalDate: z.iso.date(),
    carryoverPosition: z.number().int().positive(),
  })
  .strict();
export type CloseDayProposedNextStep = z.infer<
  typeof closeDayProposedNextStepSchema
>;

export const understandingResponseSchema = z.enum([
  "need-help",
  "still-shaky",
  "mostly-understand",
  "could-explain-it",
]);
export type UnderstandingResponse = z.infer<typeof understandingResponseSchema>;

const outcomeRollupSchema = z
  .object({
    total: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    remaining: z.number().int().nonnegative(),
  })
  .strict()
  .refine(
    (value) =>
      value.total ===
      value.done +
        value.blocked +
        value.deferred +
        value.skipped +
        value.remaining,
    "Outcome rollup parts must add to the total",
  );

const schoolworkStepSnapshotSchema = z
  .object({
    id: z.uuid(),
    assignmentId: z.uuid(),
    parentStepId: z.uuid().nullable(),
    position: z.number().int().positive(),
    instruction: nonblankText(1_000),
    completionCondition: nonblankText(2_000),
    required: z.boolean(),
    meaningfulLearning: z.boolean(),
    status: stepOutcomeStatusSchema,
    replacedBySplit: z.boolean(),
    understandingCheckEligible: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.understandingCheckEligible &&
      (!value.meaningfulLearning || value.status !== "done")
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Only completed meaningful-learning work can offer an Understanding Check",
        path: ["understandingCheckEligible"],
      });
    }
    if (value.replacedBySplit && value.status !== "deferred") {
      context.addIssue({
        code: "custom",
        message: "A split parent Step remains Deferred in preserved history",
        path: ["status"],
      });
    }
  });

const schoolworkAssignmentSnapshotSchema = z
  .object({
    id: z.uuid(),
    title: nonblankText(240),
    status: assignmentOutcomeStatusSchema,
    submissionEvidenceRequired: z.boolean(),
    required: outcomeRollupSchema,
    optional: outcomeRollupSchema,
  })
  .strict();

const outcomeHistorySchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    focusBlockId: z.uuid().nullable(),
    intent: z.enum(["done", "continue", "need-help", "split-step", "skip"]),
    actor: z.object({ role: roleSchema }).strict(),
    occurredAt: z.iso.datetime({ offset: true }),
  })
  .strict();

const helpRequestSnapshotSchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    status: z.enum(["open", "resolved"]),
    actor: z.object({ role: roleSchema }).strict(),
    createdAt: z.iso.datetime({ offset: true }),
    resolvedAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict();

const understandingCheckSnapshotSchema = z
  .object({
    id: z.uuid(),
    stepId: z.uuid(),
    response: understandingResponseSchema,
    actor: z.object({ role: z.literal("student") }).strict(),
    recordedAt: z.iso.datetime({ offset: true }),
  })
  .strict();

const submissionEvidenceSnapshotSchema = z
  .object({
    id: z.uuid(),
    assignmentId: z.uuid(),
    type: z.literal("manual"),
    sourceDescription: nonblankText(500),
    actor: z.object({ role: z.literal("parent") }).strict(),
    recordedAt: z.iso.datetime({ offset: true }),
    claimedAt: z.iso.datetime({ offset: true }).optional(),
    confidence: z.enum(["reported", "confirmed"]),
  })
  .strict();

export const skipReasonCodeSchema = z.enum([
  "unclear-directions",
  "step-too-large",
  "technical-issue",
  "schedule-conflict",
  "fatigue-unwell",
  "other",
]);
export type SkipReasonCode = z.infer<typeof skipReasonCodeSchema>;

export const skipReasonSchema = z
  .object({
    code: skipReasonCodeSchema,
    detail: nonblankText(500).optional(),
  })
  .strict();
export type SkipReason = z.infer<typeof skipReasonSchema>;

export const skipStepProtectedActionScopeSchema = z
  .object({
    action: z.literal("skip-step"),
    stepId: z.uuid(),
    assignmentId: z.uuid(),
    expectedSchoolworkRevision: z.number().int().positive(),
    expectedWorkStateRevision: z.number().int().positive(),
  })
  .strict();

export const closeDayProtectedActionScopeSchema = z
  .object({
    action: z.literal("close-day-with-unresolved-must-finish"),
    dailyQueueId: z.uuid(),
    expectedQueueRevision: z.number().int().positive(),
    expectedSchoolworkRevision: z.number().int().positive(),
    expectedWorkStateRevision: z.number().int().positive(),
    unresolvedDispositionDigest: z.string().regex(/^[a-f0-9]{64}$/u),
    reviewItems: z.array(closeDayReviewItemSchema).min(1),
    proposedNextStep: closeDayProposedNextStepSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const queueItemIds = new Set<string>();
    const stepIds = new Set<string>();
    const futurePositions = new Set<string>();
    const replanned: CloseDayProposedNextStep[] = [];
    let unresolvedMustFinish = false;
    value.reviewItems.forEach((item, index) => {
      if (queueItemIds.has(item.queueItemId)) {
        context.addIssue({
          code: "custom",
          message: "Close-day review Queue Item identifiers must be unique",
          path: ["reviewItems", index, "queueItemId"],
        });
      }
      if (stepIds.has(item.stepId)) {
        context.addIssue({
          code: "custom",
          message: "Close-day review Step identifiers must be unique",
          path: ["reviewItems", index, "stepId"],
        });
      }
      queueItemIds.add(item.queueItemId);
      stepIds.add(item.stepId);
      unresolvedMustFinish ||= item.mustFinish;
      if (item.disposition.kind === "replan") {
        const target = `${item.disposition.targetLocalDate}:${item.disposition.carryoverPosition}`;
        if (futurePositions.has(target)) {
          context.addIssue({
            code: "custom",
            message:
              "Close-day review future positions must be unique for each date",
            path: ["reviewItems", index, "disposition", "carryoverPosition"],
          });
        }
        futurePositions.add(target);
        replanned.push({
          stepId: item.stepId,
          targetLocalDate: item.disposition.targetLocalDate,
          carryoverPosition: item.disposition.carryoverPosition,
        });
      }
    });
    if (!unresolvedMustFinish) {
      context.addIssue({
        code: "custom",
        message:
          "Close-day Parent Override scope requires unresolved must-finish work",
        path: ["reviewItems"],
      });
    }
    replanned.sort(
      (left, right) =>
        left.targetLocalDate.localeCompare(right.targetLocalDate) ||
        left.carryoverPosition - right.carryoverPosition,
    );
    if (
      JSON.stringify(value.proposedNextStep) !==
      JSON.stringify(replanned[0] ?? null)
    ) {
      context.addIssue({
        code: "custom",
        message:
          "The proposed next Step must match the earliest reviewed replan",
        path: ["proposedNextStep"],
      });
    }
  });

export const protectedActionScopeSchema = z.discriminatedUnion("action", [
  skipStepProtectedActionScopeSchema,
  closeDayProtectedActionScopeSchema,
]);
export type ProtectedActionScope = z.infer<typeof protectedActionScopeSchema>;

export const closeDayOverrideReasonSchema = z
  .object({
    code: z.literal("end-of-day-closeout"),
    detail: nonblankText(500).optional(),
  })
  .strict();

export const parentOverrideReasonSchema = z.union([
  skipReasonSchema,
  closeDayOverrideReasonSchema,
]);
export type ParentOverrideReason = z.infer<typeof parentOverrideReasonSchema>;

export const parentOverrideStateSchema = z.enum([
  "requested",
  "approved",
  "denied",
  "consumed",
  "expired",
  "cancelled",
]);
export type ParentOverrideState = z.infer<typeof parentOverrideStateSchema>;

export const safeAlternativeSchema = z.enum([
  "continue",
  "need-help",
  "report-problem",
  "safety-exit",
]);

const attributedRoleSchema = z
  .object({ role: roleSchema, actorId: z.uuid() })
  .strict();

export const parentOverrideSnapshotSchema = z
  .object({
    id: z.uuid(),
    revision: z.number().int().positive(),
    state: parentOverrideStateSchema,
    scope: protectedActionScopeSchema,
    reason: parentOverrideReasonSchema,
    safeAlternatives: z
      .array(safeAlternativeSchema)
      .length(4)
      .refine((values) => new Set(values).size === 4, {
        message:
          "Safe alternatives must contain each always-allowed action once",
      }),
    requestedBy: attributedRoleSchema,
    requestedAt: z.iso.datetime({ offset: true }),
    expiresAt: z.iso.datetime({ offset: true }),
    decidedBy: z
      .object({ role: z.literal("parent"), actorId: z.uuid() })
      .strict()
      .nullable(),
    decidedAt: z.iso.datetime({ offset: true }).nullable(),
    consumedBy: attributedRoleSchema.nullable(),
    consumedAt: z.iso.datetime({ offset: true }).nullable(),
    cancelledBy: z
      .object({ role: z.literal("student"), actorId: z.uuid() })
      .strict()
      .nullable(),
    cancelledAt: z.iso.datetime({ offset: true }).nullable(),
    expiredAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const requestedAt = Date.parse(value.requestedAt);
    const expiresAt = Date.parse(value.expiresAt);
    if (requestedAt >= expiresAt) {
      context.addIssue({
        code: "custom",
        message: "Parent Override expiration must follow its request",
        path: ["expiresAt"],
      });
    }
    const actionReasonPairIsValid =
      (value.scope.action === "skip-step" &&
        value.reason.code !== "end-of-day-closeout") ||
      (value.scope.action === "close-day-with-unresolved-must-finish" &&
        value.reason.code === "end-of-day-closeout");
    if (!actionReasonPairIsValid) {
      context.addIssue({
        code: "custom",
        message: "Parent Override reason must match its protected action kind",
        path: ["reason"],
      });
    }
    const decided =
      value.state === "approved" ||
      value.state === "denied" ||
      value.state === "consumed";
    const hasDecision = value.decidedBy !== null && value.decidedAt !== null;
    if (decided && !hasDecision) {
      context.addIssue({
        code: "custom",
        message: "A Parent Override decision must preserve parent attribution",
        path: ["decidedBy"],
      });
    }
    if (
      (value.decidedBy === null) !== (value.decidedAt === null) ||
      (value.consumedBy === null) !== (value.consumedAt === null) ||
      (value.cancelledBy === null) !== (value.cancelledAt === null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Attribution and timestamp pairs must remain complete",
        path: ["state"],
      });
    }
    for (const [field, timestamp] of [
      ["decidedAt", value.decidedAt],
      ["consumedAt", value.consumedAt],
      ["cancelledAt", value.cancelledAt],
    ] as const) {
      if (
        timestamp !== null &&
        (Date.parse(timestamp) < requestedAt ||
          Date.parse(timestamp) >= expiresAt)
      ) {
        context.addIssue({
          code: "custom",
          message: "Override action time must fall within the request window",
          path: [field],
        });
      }
    }
    if (
      (value.state === "consumed") !==
      (value.consumedAt !== null && value.consumedBy !== null)
    ) {
      context.addIssue({
        code: "custom",
        message:
          "A consumed Parent Override must preserve its consumption time",
        path: ["consumedAt"],
      });
    }
    if (
      (value.state === "cancelled") !==
      (value.cancelledAt !== null && value.cancelledBy !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "A cancelled Parent Override must preserve attribution",
        path: ["cancelledAt"],
      });
    }
    if ((value.state === "expired") !== (value.expiredAt !== null)) {
      context.addIssue({
        code: "custom",
        message:
          "An expired Parent Override must preserve its expiration observation",
        path: ["expiredAt"],
      });
    }
    if (
      value.consumedAt !== null &&
      value.decidedAt !== null &&
      Date.parse(value.consumedAt) < Date.parse(value.decidedAt)
    ) {
      context.addIssue({
        code: "custom",
        message: "Consumption cannot precede the parent decision",
        path: ["consumedAt"],
      });
    }
    if (value.expiredAt !== null && Date.parse(value.expiredAt) < expiresAt) {
      context.addIssue({
        code: "custom",
        message: "Expiration observation cannot precede expiration",
        path: ["expiredAt"],
      });
    }
    const unexpectedAttribution =
      (value.state === "requested" &&
        (value.decidedAt !== null ||
          value.consumedAt !== null ||
          value.cancelledAt !== null ||
          value.expiredAt !== null)) ||
      ((value.state === "approved" || value.state === "denied") &&
        (value.decidedAt === null ||
          value.consumedAt !== null ||
          value.cancelledAt !== null ||
          value.expiredAt !== null)) ||
      (value.state === "consumed" &&
        (value.decidedAt === null ||
          value.consumedAt === null ||
          value.cancelledAt !== null ||
          value.expiredAt !== null)) ||
      (value.state === "cancelled" &&
        (value.decidedAt !== null ||
          value.consumedAt !== null ||
          value.cancelledAt === null ||
          value.expiredAt !== null)) ||
      (value.state === "expired" &&
        (value.consumedAt !== null ||
          value.cancelledAt !== null ||
          value.expiredAt === null));
    if (unexpectedAttribution) {
      context.addIssue({
        code: "custom",
        message: "Parent Override attribution must match its exact state",
        path: ["state"],
      });
    }
  });
export type ParentOverrideSnapshot = z.infer<
  typeof parentOverrideSnapshotSchema
>;

export const parentOverrideStateSnapshotSchema = z
  .object({
    revision: z.number().int().positive(),
    changedBy: roleSchema,
    changedAt: z.iso.datetime({ offset: true }),
    overrides: z.array(parentOverrideSnapshotSchema),
  })
  .strict();
export type ParentOverrideStateSnapshot = z.infer<
  typeof parentOverrideStateSnapshotSchema
>;

const parentOverrideCommandBaseSchema = mutationEnvelopeSchema.extend({
  overrideId: z.uuid(),
  expectedRevision: z.number().int().positive(),
});

export const parentOverrideCommandSchema = z.union([
  parentOverrideCommandBaseSchema
    .extend({ intent: z.enum(["approve", "deny", "cancel"]) })
    .strict(),
  parentOverrideCommandBaseSchema
    .extend({
      intent: z.literal("edit"),
      scope: protectedActionScopeSchema,
      reason: parentOverrideReasonSchema,
      expiresAt: z.iso.datetime({ offset: true }),
    })
    .strict()
    .superRefine((value, context) => {
      const actionReasonPairIsValid =
        (value.scope.action === "skip-step" &&
          value.reason.code !== "end-of-day-closeout") ||
        (value.scope.action === "close-day-with-unresolved-must-finish" &&
          value.reason.code === "end-of-day-closeout");
      if (!actionReasonPairIsValid) {
        context.addIssue({
          code: "custom",
          message:
            "Parent Override reason must match its protected action kind",
          path: ["reason"],
        });
      }
    }),
]);
export type ParentOverrideCommand = z.infer<typeof parentOverrideCommandSchema>;

export const parentOverrideCommandResultSchema = z.union([
  z
    .object({
      status: z.literal("accepted"),
      requestId: z.uuid(),
      acceptedRevision: z.number().int().positive(),
      serverTime: z.iso.datetime({ offset: true }),
      publicationSequence: z.number().int().positive(),
      snapshot: parentOverrideStateSnapshotSchema,
    })
    .strict()
    .superRefine((value, context) => {
      if (value.acceptedRevision !== value.snapshot.revision) {
        context.addIssue({
          code: "custom",
          message: "Accepted revision must match the Parent Override snapshot",
          path: ["acceptedRevision"],
        });
      }
      if (value.serverTime !== value.snapshot.changedAt) {
        context.addIssue({
          code: "custom",
          message: "Server time must anchor the Parent Override snapshot",
          path: ["serverTime"],
        });
      }
    }),
  z
    .object({
      status: z.literal("rejected"),
      publicationSequence: z.number().int().positive().optional(),
      error: z
        .object({
          code: z.enum([
            "REVISION_CONFLICT",
            "STATE_TRANSITION_INVALID",
            "NOT_AUTHORIZED",
            "REQUEST_ID_REUSED",
            "OVERRIDE_EXPIRED",
          ]),
          message: nonblankText(500),
          snapshot: parentOverrideStateSnapshotSchema.optional(),
        })
        .strict(),
    })
    .strict(),
]);
export type ParentOverrideCommandResult = z.infer<
  typeof parentOverrideCommandResultSchema
>;

function outcomePartFor(
  steps: z.infer<typeof schoolworkStepSnapshotSchema>[],
): z.infer<typeof outcomeRollupSchema> {
  const part = {
    total: steps.length,
    done: 0,
    blocked: 0,
    deferred: 0,
    skipped: 0,
    remaining: 0,
  };
  for (const step of steps) {
    if (step.status === "done") part.done += 1;
    else if (step.status === "blocked") part.blocked += 1;
    else if (step.status === "deferred") part.deferred += 1;
    else if (step.status === "skipped") part.skipped += 1;
    else part.remaining += 1;
  }
  return part;
}

export const schoolworkProgressSnapshotSchema = z
  .object({
    revision: z.number().int().positive(),
    changedBy: roleSchema,
    changedAt: z.iso.datetime({ offset: true }),
    assignments: z.array(schoolworkAssignmentSnapshotSchema),
    steps: z.array(schoolworkStepSnapshotSchema),
    outcomes: z.array(outcomeHistorySchema),
    helpRequests: z.array(helpRequestSnapshotSchema),
    understandingChecks: z.array(understandingCheckSnapshotSchema),
    submissionEvidence: z.array(submissionEvidenceSnapshotSchema),
  })
  .strict()
  .superRefine((value, context) => {
    const evidenceAssignments = new Set(
      value.submissionEvidence.map(({ assignmentId }) => assignmentId),
    );
    const understoodSteps = new Set(
      value.understandingChecks.map(({ stepId }) => stepId),
    );
    const stepsById = new Map(value.steps.map((step) => [step.id, step]));
    value.steps.forEach((step, index) => {
      if (step.parentStepId !== null) {
        const parent = stepsById.get(step.parentStepId);
        if (parent?.assignmentId !== step.assignmentId) {
          context.addIssue({
            code: "custom",
            message:
              "A split child must belong to its preserved parent Assignment",
            path: ["steps", index, "parentStepId"],
          });
        }
      }
      if (step.understandingCheckEligible && understoodSteps.has(step.id)) {
        context.addIssue({
          code: "custom",
          message: "A recorded Understanding Check is no longer pending",
          path: ["steps", index, "understandingCheckEligible"],
        });
      }
    });
    value.assignments.forEach((assignment, index) => {
      const effective = value.steps.filter(
        (step) => step.assignmentId === assignment.id && !step.replacedBySplit,
      );
      for (const [key, expected] of Object.entries({
        required: outcomePartFor(effective.filter((step) => step.required)),
        optional: outcomePartFor(effective.filter((step) => !step.required)),
      }) as ["required" | "optional", z.infer<typeof outcomeRollupSchema>][]) {
        if (JSON.stringify(assignment[key]) !== JSON.stringify(expected)) {
          context.addIssue({
            code: "custom",
            message: `${key} rollup must match effective Step facts`,
            path: ["assignments", index, key],
          });
        }
      }
      const allRequiredDone =
        assignment.required.total > 0 &&
        assignment.required.done === assignment.required.total;
      const hasEvidence = evidenceAssignments.has(assignment.id);
      if (
        assignment.status === "ready-to-verify" &&
        (!allRequiredDone ||
          !assignment.submissionEvidenceRequired ||
          hasEvidence)
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Ready to Verify requires all required Steps and outstanding evidence",
          path: ["assignments", index, "status"],
        });
      }
      if (
        assignment.status === "completed" &&
        (!allRequiredDone ||
          (assignment.submissionEvidenceRequired && !hasEvidence))
      ) {
        context.addIssue({
          code: "custom",
          message: "Completed requires required work and configured evidence",
          path: ["assignments", index, "status"],
        });
      }
    });
  });
export type SchoolworkProgressSnapshot = z.infer<
  typeof schoolworkProgressSnapshotSchema
>;

const outcomeCommandBaseSchema = mutationEnvelopeSchema.extend({
  expectedRevision: z.number().int().positive(),
  expectedWorkStateRevision: z.number().int().positive(),
  stepId: z.uuid(),
});

const splitChildInputSchema = z
  .object({
    id: z.uuid(),
    actionVerb: nonblankText(80),
    instruction: nonblankText(1_000),
    completionCondition: nonblankText(2_000),
  })
  .strict()
  .superRefine((value, context) => {
    const firstWord = value.instruction.trim().split(/\s+/u)[0];
    if (
      firstWord?.toLocaleLowerCase() !== value.actionVerb.toLocaleLowerCase()
    ) {
      context.addIssue({
        code: "custom",
        message: "Split child instruction must begin with its action verb",
        path: ["instruction"],
      });
    }
  });

const splitStepCommandSchema = outcomeCommandBaseSchema
  .extend({
    intent: z.literal("split-step"),
    children: z.array(splitChildInputSchema).min(2).max(5),
    nextChildStepId: z.uuid(),
  })
  .strict()
  .superRefine((value, context) => {
    if (!value.children.some(({ id }) => id === value.nextChildStepId)) {
      context.addIssue({
        code: "custom",
        message: "Choose one created child Step as the next Active Step",
        path: ["nextChildStepId"],
      });
    }
    if (
      new Set(value.children.map(({ id }) => id)).size !== value.children.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Split child Step identifiers must be unique",
        path: ["children"],
      });
    }
  });

export const schoolworkOutcomeCommandSchema = z.union([
  outcomeCommandBaseSchema.extend({ intent: z.literal("done") }).strict(),
  outcomeCommandBaseSchema.extend({ intent: z.literal("continue") }).strict(),
  outcomeCommandBaseSchema.extend({ intent: z.literal("need-help") }).strict(),
  splitStepCommandSchema,
  outcomeCommandBaseSchema
    .extend({
      intent: z.literal("skip"),
      reason: skipReasonSchema,
      overrideId: z.uuid().optional(),
    })
    .strict(),
]);

export const recordUnderstandingCheckCommandSchema = mutationEnvelopeSchema
  .extend({
    expectedRevision: z.number().int().positive(),
    intent: z.literal("record-understanding"),
    stepId: z.uuid(),
    response: understandingResponseSchema,
  })
  .strict();

export const recordSubmissionEvidenceCommandSchema = mutationEnvelopeSchema
  .extend({
    expectedRevision: z.number().int().positive(),
    assignmentId: z.uuid(),
    intent: z.literal("record-submission-evidence"),
    sourceDescription: nonblankText(500),
    confidence: z.enum(["reported", "confirmed"]),
    claimedAt: z.iso.datetime({ offset: true }).optional(),
  })
  .strict();

export const schoolworkCommandSchema = z.union([
  schoolworkOutcomeCommandSchema,
  recordUnderstandingCheckCommandSchema,
]);
export type SchoolworkCommand = z.infer<typeof schoolworkCommandSchema>;
export type RecordSubmissionEvidenceCommand = z.infer<
  typeof recordSubmissionEvidenceCommandSchema
>;

export const schoolworkCommandAcceptedResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    acceptedRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    publicationSequence: z.number().int().positive(),
    snapshot: schoolworkProgressSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.acceptedRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Accepted revision must match the schoolwork snapshot",
        path: ["acceptedRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.changedAt) {
      context.addIssue({
        code: "custom",
        message: "Server time must anchor the schoolwork snapshot",
        path: ["serverTime"],
      });
    }
  });

export const schoolworkCommandResultSchema = z.union([
  schoolworkCommandAcceptedResultSchema,
  z
    .object({
      status: z.literal("override-required"),
      requestId: z.uuid(),
      serverTime: z.iso.datetime({ offset: true }),
      publicationSequence: z.number().int().positive(),
      override: parentOverrideSnapshotSchema,
      safeAlternatives: z.array(safeAlternativeSchema).length(4),
    })
    .strict(),
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("REVISION_CONFLICT"),
          currentRevision: z.number().int().positive(),
          changedBy: roleSchema,
          changedAt: z.iso.datetime({ offset: true }),
          snapshot: schoolworkProgressSnapshotSchema,
        })
        .strict(),
    })
    .strict()
    .superRefine((value, context) => {
      if (value.error.currentRevision !== value.error.snapshot.revision) {
        context.addIssue({
          code: "custom",
          message: "Conflict revision must match the schoolwork snapshot",
          path: ["error", "currentRevision"],
        });
      }
    }),
  z
    .object({
      status: z.literal("rejected"),
      publicationSequence: z.number().int().positive().optional(),
      error: z
        .object({
          code: z.enum([
            "VALIDATION_FAILED",
            "STATE_TRANSITION_INVALID",
            "NOT_AUTHORIZED",
            "REQUEST_ID_REUSED",
            "OVERRIDE_MISMATCH",
            "OVERRIDE_EXPIRED",
          ]),
          message: nonblankText(500),
        })
        .strict(),
    })
    .strict(),
]);
export type SchoolworkCommandResult = z.infer<
  typeof schoolworkCommandResultSchema
>;

export const schoolworkSemanticEventTypeSchema = z.enum([
  "AssignmentCompletionChanged",
  "DailyQueueChanged",
  "ActiveStepChanged",
  "FocusBlockChanged",
  "TransitionChanged",
  "RecoveryRecorded",
  "HelpRequestChanged",
  "OverrideChanged",
]);
export type SchoolworkSemanticEventType = z.infer<
  typeof schoolworkSemanticEventTypeSchema
>;

export const schoolworkAffectedAggregateSchema = z.enum([
  "SchoolworkProgress",
  "StudentWorkState",
  "FocusBlock",
  "DailyQueue",
  "HelpRequest",
  "UnderstandingCheck",
  "SubmissionEvidence",
  "ParentOverride",
]);
export type SchoolworkAffectedAggregate = z.infer<
  typeof schoolworkAffectedAggregateSchema
>;

export const schoolworkProgressChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: schoolworkSemanticEventTypeSchema,
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    semanticEvents: z.array(schoolworkSemanticEventTypeSchema).min(1),
    affectedAggregates: z.array(schoolworkAffectedAggregateSchema).min(1),
    snapshot: schoolworkProgressSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (
      !value.semanticEvents.includes(value.type) ||
      new Set(value.semanticEvents).size !== value.semanticEvents.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Semantic events must be unique and include the envelope type",
        path: ["semanticEvents"],
      });
    }
    if (
      !value.affectedAggregates.includes("SchoolworkProgress") ||
      new Set(value.affectedAggregates).size !== value.affectedAggregates.length
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Affected aggregates must be unique and include SchoolworkProgress",
        path: ["affectedAggregates"],
      });
    }
    if (
      value.resultingRevision !== value.snapshot.revision ||
      value.baseRevision >= value.resultingRevision
    ) {
      context.addIssue({
        code: "custom",
        message: "Schoolwork event revisions must match and advance",
        path: ["resultingRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.changedAt) {
      context.addIssue({
        code: "custom",
        message: "Event time must anchor the schoolwork snapshot",
        path: ["serverTime"],
      });
    }
  });
export type SchoolworkProgressChangedEvent = z.infer<
  typeof schoolworkProgressChangedEventSchema
>;

export const parentOverrideChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    requestId: z.uuid(),
    type: z.literal("OverrideChanged"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    change: z.enum([
      "requested",
      "edited",
      "approved",
      "denied",
      "consumed",
      "expired",
      "cancelled",
    ]),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: parentOverrideStateSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.snapshot.revision) {
      context.addIssue({
        code: "custom",
        message: "Resulting revision must match the Parent Override snapshot",
        path: ["resultingRevision"],
      });
    }
    if (value.requestId !== value.eventId) {
      context.addIssue({
        code: "custom",
        message: "Override event identity must preserve request correlation",
        path: ["requestId"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Resulting Parent Override revision must follow its base",
        path: ["baseRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.changedAt) {
      context.addIssue({
        code: "custom",
        message: "Event time must anchor the Parent Override snapshot",
        path: ["serverTime"],
      });
    }
  });
export type ParentOverrideChangedEvent = z.infer<
  typeof parentOverrideChangedEventSchema
>;

export const queueItemCloseoutDispositionSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("replan"),
      futureLocalDate: z.iso.date(),
      futurePosition: z.number().int().positive(),
    })
    .strict(),
  z.object({ kind: z.literal("defer") }).strict(),
  z.object({ kind: z.literal("retain") }).strict(),
  z.object({ kind: z.literal("parent-cancelled") }).strict(),
]);
export type QueueItemCloseoutDisposition = z.infer<
  typeof queueItemCloseoutDispositionSchema
>;

export const dailyQueueCloseoutItemSchema = z
  .object({
    queueItemId: z.uuid(),
    stepId: z.uuid(),
    assignmentId: z.uuid(),
    courseTitle: nonblankText(240),
    assignmentTitle: nonblankText(240),
    stepInstruction: nonblankText(1_000),
    positionAtClose: z.number().int().positive(),
    mustFinish: z.boolean(),
    statusAtClose: queueItemStatusAtCloseSchema,
    disposition: queueItemCloseoutDispositionSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    const finished = value.statusAtClose.step === "done";
    if (finished !== (value.disposition === null)) {
      context.addIssue({
        code: "custom",
        message:
          "Only a Done Queue Item omits disposition; every unfinished item requires one",
        path: ["disposition"],
      });
    }
  });
export type DailyQueueCloseoutItem = z.infer<
  typeof dailyQueueCloseoutItemSchema
>;

export const dailyQueueCloseoutTimeSummarySchema = z
  .object({
    plannedMinimumMinutes: z.number().int().nonnegative(),
    plannedMaximumMinutes: z.number().int().nonnegative(),
    focusBlockTimerRunningDurationMs: z.number().int().nonnegative(),
    breakDurationMs: z.number().int().nonnegative(),
    transitionDurationMs: z.number().int().nonnegative(),
  })
  .strict()
  .refine(
    (value) => value.plannedMaximumMinutes >= value.plannedMinimumMinutes,
    {
      message: "Planned maximum must be at least the planned minimum",
      path: ["plannedMaximumMinutes"],
    },
  );
export type DailyQueueCloseoutTimeSummary = z.infer<
  typeof dailyQueueCloseoutTimeSummarySchema
>;

export const dailyQueueCloseoutPreviewItemSchema = z
  .object({
    queueItemId: z.uuid(),
    stepId: z.uuid(),
    assignmentId: z.uuid(),
    courseTitle: nonblankText(240),
    assignmentTitle: nonblankText(240),
    stepInstruction: nonblankText(1_000),
    positionAtClose: z.number().int().positive(),
    mustFinish: z.boolean(),
    statusAtClose: queueItemStatusAtCloseSchema,
  })
  .strict();
export type DailyQueueCloseoutPreviewItem = z.infer<
  typeof dailyQueueCloseoutPreviewItemSchema
>;

export const dailyQueueCloseoutPreviewSchema = z
  .object({
    dailyQueueId: z.uuid(),
    queueRevision: z.number().int().positive(),
    schoolworkRevision: z.number().int().positive(),
    workStateRevision: z.number().int().positive(),
    localDate: z.iso.date(),
    timezone: timezoneSchema,
    items: z.array(dailyQueueCloseoutPreviewItemSchema),
    unresolvedHelpRequestIds: z.array(z.uuid()),
    timeSummary: dailyQueueCloseoutTimeSummarySchema,
  })
  .strict()
  .superRefine((value, context) => {
    const queueItemIds = new Set<string>();
    const stepIds = new Set<string>();
    value.items.forEach((item, index) => {
      if (item.positionAtClose !== index + 1) {
        context.addIssue({
          code: "custom",
          message: "Preview positions must preserve the Daily Queue order",
          path: ["items", index, "positionAtClose"],
        });
      }
      if (queueItemIds.has(item.queueItemId) || stepIds.has(item.stepId)) {
        context.addIssue({
          code: "custom",
          message: "Preview Queue Item and Step identities must be unique",
          path: ["items", index],
        });
      }
      queueItemIds.add(item.queueItemId);
      stepIds.add(item.stepId);
    });
    if (
      new Set(value.unresolvedHelpRequestIds).size !==
      value.unresolvedHelpRequestIds.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Unresolved help requests must be unique",
        path: ["unresolvedHelpRequestIds"],
      });
    }
  });
export type DailyQueueCloseoutPreview = z.infer<
  typeof dailyQueueCloseoutPreviewSchema
>;

export const dailyQueueCloseoutSnapshotSchema = z
  .object({
    id: z.uuid(),
    dailyQueueId: z.uuid(),
    queueRevision: z.number().int().positive(),
    sourceQueueRevision: z.number().int().positive(),
    sourceSchoolworkRevision: z.number().int().positive(),
    sourceWorkStateRevision: z.number().int().positive(),
    localDate: z.iso.date(),
    timezone: timezoneSchema,
    closedAt: z.iso.datetime({ offset: true }),
    closedBy: attributedRoleSchema,
    items: z.array(dailyQueueCloseoutItemSchema),
    nextPlannedStep: z
      .object({
        stepId: z.uuid(),
        localDate: z.iso.date(),
        position: z.number().int().positive(),
      })
      .strict()
      .nullable(),
    unresolvedHelpRequestIds: z.array(z.uuid()),
    timeSummary: dailyQueueCloseoutTimeSummarySchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.queueRevision !== value.sourceQueueRevision + 1) {
      context.addIssue({
        code: "custom",
        message: "Closing a Daily Queue advances its revision exactly once",
        path: ["queueRevision"],
      });
    }

    const seenQueueItems = new Set<string>();
    const seenSteps = new Set<string>();
    const seenHelpRequests = new Set<string>();
    const seenFuturePositions = new Set<string>();
    const replanned: Array<{
      stepId: string;
      localDate: string;
      position: number;
    }> = [];
    value.items.forEach((item, index) => {
      if (item.positionAtClose !== index + 1) {
        context.addIssue({
          code: "custom",
          message: "Closeout positions must preserve the Daily Queue order",
          path: ["items", index, "positionAtClose"],
        });
      }
      for (const [identity, seen, path] of [
        [item.queueItemId, seenQueueItems, "queueItemId"],
        [item.stepId, seenSteps, "stepId"],
      ] as const) {
        if (seen.has(identity)) {
          context.addIssue({
            code: "custom",
            message: "Closeout Queue Item and Step identities must be unique",
            path: ["items", index, path],
          });
        }
        seen.add(identity);
      }
      if (item.disposition?.kind === "replan") {
        if (item.disposition.futureLocalDate <= value.localDate) {
          context.addIssue({
            code: "custom",
            message: "Replanned work requires a future local date",
            path: ["items", index, "disposition", "futureLocalDate"],
          });
        }
        const target = `${item.disposition.futureLocalDate}:${item.disposition.futurePosition}`;
        if (seenFuturePositions.has(target)) {
          context.addIssue({
            code: "custom",
            message:
              "Replanned Queue Items need unique positions on each future local date",
            path: ["items", index, "disposition", "futurePosition"],
          });
        }
        seenFuturePositions.add(target);
        replanned.push({
          stepId: item.stepId,
          localDate: item.disposition.futureLocalDate,
          position: item.disposition.futurePosition,
        });
      }
    });
    value.unresolvedHelpRequestIds.forEach((helpRequestId, index) => {
      if (seenHelpRequests.has(helpRequestId)) {
        context.addIssue({
          code: "custom",
          message: "Unresolved help requests must be unique",
          path: ["unresolvedHelpRequestIds", index],
        });
      }
      seenHelpRequests.add(helpRequestId);
    });
    if (
      value.closedBy.role !== "parent" &&
      value.items.some(
        ({ disposition }) => disposition?.kind === "parent-cancelled",
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Only a parent may cancel unfinished work",
        path: ["closedBy", "role"],
      });
    }

    replanned.sort(
      (left, right) =>
        left.localDate.localeCompare(right.localDate) ||
        left.position - right.position,
    );
    const expectedNext = replanned[0] ?? null;
    if (
      JSON.stringify(value.nextPlannedStep) !== JSON.stringify(expectedNext)
    ) {
      context.addIssue({
        code: "custom",
        message:
          "The proposed next Step must be the first explicitly replanned item",
        path: ["nextPlannedStep"],
      });
    }
  });
export type DailyQueueCloseoutSnapshot = z.infer<
  typeof dailyQueueCloseoutSnapshotSchema
>;

export const queueItemCloseoutDecisionSchema = z
  .object({
    queueItemId: z.uuid(),
    disposition: queueItemCloseoutDispositionSchema,
  })
  .strict();
export type QueueItemCloseoutDecision = z.infer<
  typeof queueItemCloseoutDecisionSchema
>;

export const closeDailyQueueCommandSchema = mutationEnvelopeSchema
  .extend({
    expectedRevision: z.number().int().positive(),
    intent: z.literal("close-day"),
    queueId: z.uuid(),
    expectedSchoolworkRevision: z.number().int().positive(),
    expectedWorkStateRevision: z.number().int().positive(),
    dispositions: z.array(queueItemCloseoutDecisionSchema),
    overrideId: z.uuid().optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const queueItemIds = new Set<string>();
    const futurePositions = new Set<string>();
    value.dispositions.forEach((decision, index) => {
      if (queueItemIds.has(decision.queueItemId)) {
        context.addIssue({
          code: "custom",
          message: "Each Queue Item may have only one closeout disposition",
          path: ["dispositions", index, "queueItemId"],
        });
      }
      queueItemIds.add(decision.queueItemId);
      if (decision.disposition.kind === "replan") {
        const target = `${decision.disposition.futureLocalDate}:${decision.disposition.futurePosition}`;
        if (futurePositions.has(target)) {
          context.addIssue({
            code: "custom",
            message:
              "Replanned Queue Items need unique positions on each future local date",
            path: ["dispositions", index, "disposition", "futurePosition"],
          });
        }
        futurePositions.add(target);
      }
    });
  });
export type CloseDailyQueueCommand = z.infer<
  typeof closeDailyQueueCommandSchema
>;

export const dailyQueueCloseoutCommandResultSchema = z.union([
  z
    .object({
      status: z.literal("accepted"),
      requestId: z.uuid(),
      acceptedRevision: z.number().int().positive(),
      serverTime: z.iso.datetime({ offset: true }),
      publicationSequence: z.number().int().positive(),
      snapshot: dailyQueueCloseoutSnapshotSchema,
    })
    .strict()
    .superRefine((value, context) => {
      if (value.acceptedRevision !== value.snapshot.queueRevision) {
        context.addIssue({
          code: "custom",
          message: "Accepted revision must match the closed Daily Queue",
          path: ["acceptedRevision"],
        });
      }
      if (value.serverTime !== value.snapshot.closedAt) {
        context.addIssue({
          code: "custom",
          message: "Server time must anchor the immutable closeout",
          path: ["serverTime"],
        });
      }
    }),
  z
    .object({
      status: z.literal("override-required"),
      requestId: z.uuid(),
      serverTime: z.iso.datetime({ offset: true }),
      publicationSequence: z.number().int().positive(),
      override: parentOverrideSnapshotSchema,
      safeAlternatives: z.array(safeAlternativeSchema).length(4),
    })
    .strict()
    .superRefine((value, context) => {
      if (
        value.override.scope.action !== "close-day-with-unresolved-must-finish"
      ) {
        context.addIssue({
          code: "custom",
          message: "Closeout requires the exact unresolved must-finish scope",
          path: ["override", "scope"],
        });
      }
      if (
        JSON.stringify(value.safeAlternatives) !==
        JSON.stringify(value.override.safeAlternatives)
      ) {
        context.addIssue({
          code: "custom",
          message: "Safe alternatives must match the Parent Override",
          path: ["safeAlternatives"],
        });
      }
    }),
  z
    .object({
      status: z.literal("rejected"),
      error: z
        .object({
          code: z.literal("REVISION_CONFLICT"),
          currentRevision: z.number().int().positive(),
          changedBy: roleSchema,
          changedAt: z.iso.datetime({ offset: true }),
          snapshot: dailyQueueSnapshotSchema,
        })
        .strict(),
    })
    .strict()
    .superRefine((value, context) => {
      if (value.error.currentRevision !== value.error.snapshot.revision) {
        context.addIssue({
          code: "custom",
          message: "Conflict revision must match the current Daily Queue",
          path: ["error", "currentRevision"],
        });
      }
    }),
  z
    .object({
      status: z.literal("rejected"),
      publicationSequence: z.number().int().positive().optional(),
      error: z
        .object({
          code: z.enum([
            "VALIDATION_FAILED",
            "STATE_TRANSITION_INVALID",
            "NOT_AUTHORIZED",
            "REQUEST_ID_REUSED",
            "OVERRIDE_MISMATCH",
            "OVERRIDE_EXPIRED",
            "OFFLINE_OR_UNAVAILABLE",
          ]),
          message: nonblankText(500),
          issues: z.array(validationIssueSchema).min(1).optional(),
        })
        .strict(),
    })
    .strict(),
]);
export type DailyQueueCloseoutCommandResult = z.infer<
  typeof dailyQueueCloseoutCommandResultSchema
>;

export const dayClosedEventSchema = z
  .object({
    eventId: z.uuid(),
    requestId: z.uuid(),
    type: z.literal("DayClosed"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: dailyQueueCloseoutSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.baseRevision !== value.snapshot.sourceQueueRevision ||
      value.resultingRevision !== value.snapshot.queueRevision
    ) {
      context.addIssue({
        code: "custom",
        message: "DayClosed event revisions must match the immutable closeout",
        path: ["resultingRevision"],
      });
    }
    if (value.serverTime !== value.snapshot.closedAt) {
      context.addIssue({
        code: "custom",
        message: "DayClosed event time must anchor the immutable closeout",
        path: ["serverTime"],
      });
    }
    if (value.baseRevision >= value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Closing a Daily Queue must advance its revision",
        path: ["baseRevision"],
      });
    }
  });
export type DayClosedEvent = z.infer<typeof dayClosedEventSchema>;

export const householdSnapshotSchema = z
  .object({
    role: roleSchema,
    family: z.object({ id: z.uuid(), label: z.string().min(1) }),
    student: z.object({
      id: z.uuid(),
      displayName: z.string().min(1),
      timezone: z.string().min(1),
      accountabilityMode: accountabilityModeSchema,
      supportLevel: supportLevelSchema,
      policyRevision: z.number().int().positive(),
      accessRevision: z.number().int().positive(),
      eventSequence: z.number().int().nonnegative(),
    }),
    dailyQueue: dailyQueueSnapshotSchema.nullable(),
    studentWorkState: studentWorkStateSnapshotSchema.nullable(),
    focusBlock: focusBlockSnapshotSchema.nullable(),
    studentRecovery: studentRecoverySnapshotSchema.nullable().default(null),
    schoolworkProgress: schoolworkProgressSnapshotSchema.nullable().optional(),
    parentOverrides: parentOverrideStateSnapshotSchema.nullable().optional(),
    dayCloseout: dailyQueueCloseoutSnapshotSchema.nullable().optional(),
  })
  .superRefine((value, context) => {
    const state = value.studentWorkState;
    const projectedActiveQueue =
      value.dailyQueue?.status === "active" ? value.dailyQueue : null;

    if (value.role === "parent" && state === null) return;

    if (state === null || state.activeQueueId === null) {
      if (projectedActiveQueue !== null) {
        context.addIssue({
          code: "custom",
          message:
            "Student Work State must identify the projected Active Daily Queue",
          path: ["studentWorkState"],
        });
      }
      if (value.focusBlock !== null) {
        context.addIssue({
          code: "custom",
          message: "A Focus Block requires an Active Step",
          path: ["focusBlock"],
        });
      }
      return;
    }
    if (
      projectedActiveQueue?.id !== state.activeQueueId ||
      projectedActiveQueue.revision !== state.activeQueueRevision
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Student Work State must reference the projected Active Daily Queue",
        path: ["studentWorkState", "activeQueueId"],
      });
      return;
    }

    if (state.activeStepId !== null && state.activeQueueItemId !== null) {
      const activeItem = projectedActiveQueue.queueItems.find(
        ({ id }) => id === state.activeQueueItemId,
      );
      if (activeItem?.stepId !== state.activeStepId) {
        context.addIssue({
          code: "custom",
          message:
            "Active Step must match its Queue Item in the projected Daily Queue",
          path: ["studentWorkState", "activeStepId"],
        });
      }
    }
    if (
      value.focusBlock !== null &&
      value.focusBlock.stepId !== state.activeStepId
    ) {
      context.addIssue({
        code: "custom",
        message: "The current Focus Block must belong to the Active Step",
        path: ["focusBlock", "stepId"],
      });
    }
    if (
      value.studentRecovery?.break !== null &&
      value.studentRecovery?.break !== undefined &&
      value.studentRecovery.break.returnStepId !== state.activeStepId
    ) {
      context.addIssue({
        code: "custom",
        message: "The latest Break must preserve the Active Step",
        path: ["studentRecovery", "break", "returnStepId"],
      });
    }
    if (
      value.studentRecovery?.transition !== null &&
      value.studentRecovery?.transition !== undefined &&
      value.studentRecovery.transition.returnStepId !== state.activeStepId
    ) {
      context.addIssue({
        code: "custom",
        message: "The latest transition must preserve the Active Step",
        path: ["studentRecovery", "transition", "returnStepId"],
      });
    }
  });
export type HouseholdSnapshot = z.infer<typeof householdSnapshotSchema>;

export const surfaceKindSchema = z.enum([
  "parent-dashboard",
  "student-console",
  "managed-workspace",
]);
export type SurfaceKind = z.infer<typeof surfaceKindSchema>;

export const studentEventTypeSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[A-Z][A-Za-z0-9]*$/, "Student event types use privacy-safe names");
export type StudentEventType = z.infer<typeof studentEventTypeSchema>;

export const policyChangedEventSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("PolicyChanged"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: z
      .object({
        accountabilityMode: accountabilityModeSchema,
        supportLevel: supportLevelSchema,
        policyRevision: z.number().int().positive(),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.resultingRevision !== value.sequence ||
      value.baseRevision + 1 !== value.resultingRevision
    ) {
      context.addIssue({
        code: "custom",
        message: "Policy publications must use one contiguous Student revision",
        path: ["resultingRevision"],
      });
    }
    if (value.policyRevision !== value.snapshot.policyRevision) {
      context.addIssue({
        code: "custom",
        message: "Published policy and snapshot revisions must match",
        path: ["policyRevision"],
      });
    }
  });
export type PolicyChangedEvent = z.infer<typeof policyChangedEventSchema>;

export const studentEventNotificationSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("StudentEvent"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().positive(),
    baseRevision: z.number().int().nonnegative(),
    resultingRevision: z.number().int().positive(),
    policyRevision: z.number().int().positive(),
    actor: z.object({ role: roleSchema }).strict(),
    serverTime: z.iso.datetime({ offset: true }),
    eventType: studentEventTypeSchema,
  })
  .strict()
  .superRefine((value, context) => {
    if (value.resultingRevision !== value.sequence) {
      context.addIssue({
        code: "custom",
        message:
          "The resulting Student revision must equal the publication sequence",
        path: ["resultingRevision"],
      });
    }
    if (value.baseRevision + 1 !== value.resultingRevision) {
      context.addIssue({
        code: "custom",
        message: "Student publication revisions must be contiguous",
        path: ["baseRevision"],
      });
    }
  });
export type StudentEventNotification = z.infer<
  typeof studentEventNotificationSchema
>;

export const studentSnapshotNotificationSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("StudentSnapshot"),
    schemaVersion: z.literal(1),
    sequence: z.number().int().nonnegative(),
    aggregateRevision: z.number().int().nonnegative(),
    policyRevision: z.number().int().positive(),
    serverTime: z.iso.datetime({ offset: true }),
    snapshot: householdSnapshotSchema,
  })
  .strict()
  .superRefine((value, context) => {
    const authoritativeSequence = value.snapshot.student.eventSequence;
    if (
      value.sequence !== authoritativeSequence ||
      value.aggregateRevision !== authoritativeSequence
    ) {
      context.addIssue({
        code: "custom",
        message:
          "Snapshot sequence and Student revision must share one authoritative anchor",
        path: ["sequence"],
      });
    }
    if (value.policyRevision !== value.snapshot.student.policyRevision) {
      context.addIssue({
        code: "custom",
        message: "Snapshot policy revision must match the Student policy",
        path: ["policyRevision"],
      });
    }
  });
export type StudentSnapshotNotification = z.infer<
  typeof studentSnapshotNotificationSchema
>;

export const snapshotRequiredNotificationSchema = z
  .object({
    eventId: z.uuid(),
    type: z.literal("SnapshotRequired"),
    schemaVersion: z.literal(1),
    expectedSequence: z.number().int().nonnegative(),
    currentSequence: z.number().int().nonnegative(),
    reason: z.enum(["sequence-gap", "revision-gap", "schema-incompatible"]),
    serverTime: z.iso.datetime({ offset: true }),
  })
  .strict();
export type SnapshotRequiredNotification = z.infer<
  typeof snapshotRequiredNotificationSchema
>;

export const studentSyncMessageSchema = z.union([
  studentSnapshotNotificationSchema,
  studentEventNotificationSchema,
  snapshotRequiredNotificationSchema,
]);
export type StudentSyncMessage = z.infer<typeof studentSyncMessageSchema>;

export const surfaceAcknowledgementCommandSchema = z
  .object({
    requestId: z.uuid(),
    surfaceKind: surfaceKindSchema,
    eventSequence: z.number().int().nonnegative(),
    aggregateRevision: z.number().int().nonnegative(),
    policyRevision: z.number().int().positive(),
    observedAtClient: z.iso.datetime({ offset: true }).optional(),
  })
  .strict()
  .refine((value) => value.eventSequence === value.aggregateRevision, {
    message: "Acknowledged sequence and Student revision must match",
    path: ["aggregateRevision"],
  });
export type SurfaceAcknowledgementCommand = z.infer<
  typeof surfaceAcknowledgementCommandSchema
>;

export const surfaceAcknowledgementResultSchema = z
  .object({
    status: z.literal("accepted"),
    requestId: z.uuid(),
    surfaceKind: surfaceKindSchema,
    eventSequence: z.number().int().nonnegative(),
    aggregateRevision: z.number().int().nonnegative(),
    policyRevision: z.number().int().positive(),
    acknowledgedAt: z.iso.datetime({ offset: true }),
  })
  .strict()
  .refine((value) => value.eventSequence === value.aggregateRevision, {
    message: "Acknowledged sequence and Student revision must match",
    path: ["aggregateRevision"],
  });
export type SurfaceAcknowledgementResult = z.infer<
  typeof surfaceAcknowledgementResultSchema
>;

export const policyDeliveryStatusSchema = z
  .object({
    surfaceKind: z.enum(["student-console", "managed-workspace"]),
    status: z.enum(["connected", "pending", "offline"]),
    currentPolicyRevision: z.number().int().positive(),
    acknowledgedPolicyRevision: z.number().int().positive().nullable(),
    acknowledgedEventSequence: z.number().int().nonnegative().nullable(),
    lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.status === "connected") {
      if (
        value.acknowledgedPolicyRevision !== value.currentPolicyRevision ||
        value.acknowledgedEventSequence === null ||
        value.lastSeenAt === null
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Connected means the current policy was acknowledged by a present surface",
          path: ["status"],
        });
      }
    }
  });
export type PolicyDeliveryStatus = z.infer<typeof policyDeliveryStatusSchema>;
